import React, { useEffect, useRef, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
// ChartRendererSupabase is already imported above in this file; keep single import
import { useLocation } from '../../contexts/LocationContext';
import { supabaseClient } from '../../services/supabase';
import { setBuildingsEnergyOverrideFromEnergyPage } from '../../services/buildingsEnergyDataService';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import { DataTable } from '../charts/DataTable';
import { fetchElectricitySeries, fetchNaturalGasSeries } from '../charts/EnergyUseSupabase';
import { HeatPumpCostChart } from '../charts/HeatPumpCostChart';
import { HeatPumpTechnologyChart } from '../charts/HeatPumpTechnologyChart';
import { HeatPumpUnitsChart } from '../charts/HeatPumpUnitsChart';
import { HeatPumpPerCapitaChart } from '../charts/HeatPumpPerCapitaChart';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import fetchSolarPVCapacitySupabase from '../charts/SolarPVCapacitySupabase';
import fetchSolarPVCostSupabase from '../charts/SolarPVCostSupabase';
import fetchSolarPVProjectCountSupabase from '../charts/SolarPVProjectCountSupabase';
import fetchSolarPVBatteryCostSupabase from '../charts/SolarPVBatteryCostSupabase';
import fetchSolarPVInstallersSupabase from '../charts/SolarPVInstallersSupabase';
import fetchSolarPVInstallersCostSupabase from '../charts/SolarPVInstallersCostSupabase';
import { PageHeader } from '../layout/PageHeader';
import { CumulativeSolarCapacityChart } from '../charts/CumulativeSolarCapacityChart';
import { SolarPerCapitaComparisonChart } from '../charts/SolarPerCapitaComparisonChart';
import CarbonIntensityChart from '../charts/CarbonIntensityChart';
import BayRENMultifamilyTargetingTool from '../../domains/bayren/components/BayRENMultifamilyTargetingTool';
import CaliforniaBayRENTool from '../../domains/bayren/components/CaliforniaBayRENTool';
import { DataReviewBanner } from '../charts/DataReviewBanner';
import { isElectricityDataUnderReview, getLocationNameUnderReview, isElectricityDataUnderReviewByName, getGeofipsFromLocationName } from '../../utils/dataReviewLocations';
import { HousingToEmployees } from '../charts/HousingToEmployees';
import GenericMapTiered from '../charts/GenericMapTiered';
import SingleFamilyDetached from '../charts/SingleFamilyDetached';
import MarketRentChart from '../charts/MarketRentChart';

interface EnergyProps {
  location?: string;
}

// Function to calculate stacked data as sum of residential + commercial
const calculateStackedData = (resData: any, comData: any, type: string) => {
  if (!resData?.chartData || !comData?.chartData) return null;

  const resLabels = (resData.chartData.labels || []) as number[];
  const comLabels = (comData.chartData.labels || []) as number[];
  
  // Get years that exist in both residential and commercial data
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const allYears: number[] = Array.from(resYears).filter((year: number) => comYears.has(year)).sort();
  
  // Extract data from all datasets (observed, interpolated, extrapolated) for each sector
  const resDatasets = resData.chartData.datasets || [];
  const comDatasets = comData.chartData.datasets || [];
  
  // Create maps for each sector's total values (sum of all data types)
  const resMap = new Map<number, number>();
  const comMap = new Map<number, number>();
  
  // Sum up all data types for residential
  resLabels.forEach((year: number, i: number) => {
    let total = 0;
    resDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    resMap.set(year, total);
  });
  
  // Sum up all data types for commercial
  comLabels.forEach((year: number, i: number) => {
    let total = 0;
    comDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    comMap.set(year, total);
  });

  // Calculate stacked values
  const stackedValues = allYears.map(year => 
    (resMap.get(year) as number || 0) + (comMap.get(year) as number || 0)
  );

  // Create stacked chart data
  const stackedChartData = {
    labels: allYears,
    datasets: [
      {
        label: 'Residential',
        data: allYears.map(year => resMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderWidth: 1,
        stack: 'total'
      },
      {
        label: 'Commercial',
        data: allYears.map(year => comMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderWidth: 1,
        stack: 'total'
      }
    ]
  };

  // Create chart options for stacked bar chart
  const stackedChartOptions = {
    type: 'bar',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          padding: 10,
          usePointStyle: true,
          pointStyle: 'rect'
        }
      },
      title: { display: true, text: `Total ${type} (Stacked)` },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const v = Number(ctx.parsed.y);
            return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
          }
        }
      }
    },
    scales: {
      x: { 
        stacked: true, 
        title: { display: true, text: 'Year' } 
      },
      y: { 
        stacked: true, 
        beginAtZero: true, 
        title: { 
          display: true, 
          text: type === 'Electricity' ? 'kWh' : 'Therms' 
        }, 
        ticks: { 
          callback: (v: any) => Number(v).toLocaleString() 
        } 
      }
    },
    layout: {
      padding: {
        bottom: 20
      }
    }
  };

  return {
    chartData: stackedChartData,
    chartOptions: stackedChartOptions,
    latestYear: Math.max(...allYears),
    latestValue: stackedValues[stackedValues.length - 1] || 0
  };
};

export const Energy: React.FC<EnergyProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips, coreDataLoading } = useLocation();
  const [marketRentChartView, setMarketRentChartView] = useState<'line' | 'stacked'>('stacked');
  const [capacityData, setCapacityData] = useState<any>(null);
  const [costData, setCostData] = useState<any>(null);
  const [projectCountData, setProjectCountData] = useState<any>(null);
  const [elecRes, setElecRes] = useState<any>(null);
  const [elecCom, setElecCom] = useState<any>(null);
  const [elecStacked, setElecStacked] = useState<any>(null);
  const [gasRes, setGasRes] = useState<any>(null);
  const [gasCom, setGasCom] = useState<any>(null);
  const [gasStacked, setGasStacked] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>('overview');
  const [elecView, setElecView] = useState<'res' | 'com' | 'total' | 'perCapita'>('total');
  const [gasView, setGasView] = useState<'res' | 'com' | 'total' | 'perCapita'>('total');
  const [elecMethodology, setElecMethodology] = useState<{dataSources: string[], methods: string[]} | null>(null);
  const [gasMethodology, setGasMethodology] = useState<{dataSources: string[], methods: string[]} | null>(null);
  
  // Chart refs for download functionality
  const elecChartRef = useRef<HTMLCanvasElement>(null);
  const gasChartRef = useRef<HTMLCanvasElement>(null);
  const [pvView, setPvView] = useState<'capacity' | 'cost' | 'cumulative' | 'perCapita' | 'batteryCost' | 'installers' | 'installersCost'>('capacity');
  const [batteryCostData, setBatteryCostData] = useState<any>(null);
  const [installersData, setInstallersData] = useState<any>(null);
  const [installersCostData, setInstallersCostData] = useState<any>(null);
  
  // Data table state for electricity charts
  const [elecShowDataTable, setElecShowDataTable] = useState(false);
  const [elecTableData, setElecTableData] = useState<any[]>([]);
  const [elecOriginalData, setElecOriginalData] = useState<any[]>([]);
  const [elecModifiedYears, setElecModifiedYears] = useState<Set<number>>(new Set());
  
  // Data table state for natural gas charts
  const [gasShowDataTable, setGasShowDataTable] = useState(false);
  const [gasTableData, setGasTableData] = useState<any[]>([]);
  const [gasOriginalData, setGasOriginalData] = useState<any[]>([]);
  const [gasModifiedYears, setGasModifiedYears] = useState<Set<number>>(new Set());
  
  // Per capita data state
  const [elecPerCapitaData, setElecPerCapitaData] = useState<any>(null);
  const [gasPerCapitaData, setGasPerCapitaData] = useState<any>(null);
  const [californiaElecPerCapita, setCaliforniaElecPerCapita] = useState<any>(null);
  const [californiaGasPerCapita, setCaliforniaGasPerCapita] = useState<any>(null);
  const [heatPumpMetrics, setHeatPumpMetrics] = useState<any>(null);
  const [heatPumpView, setHeatPumpView] = useState<'cost' | 'technology' | 'units' | 'perCapita'>('units');
  const [activeCarbonIntensityView, setActiveCarbonIntensityView] = useState<string>('overview');
  const [ownerOccupiedHousingMetrics, setOwnerOccupiedHousingMetrics] = useState<{
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  } | null>(null);
  const [activeOwnerOccupiedView, setActiveOwnerOccupiedView] = useState<string>('overview');

  const currentLocation = propLocation || selectedLocation;

  // Function to calculate heat pump metrics
  const calculateHeatPumpMetrics = async (location: string, geofips: number) => {
    try {
      if (!geofips) return null;

      // Fetch heat pump data for the location
      const { data: locationData, error: locationError } = await supabaseClient
        .from('heat_pumps')
        .select('year, system, units_installed, project_cost_per_unit_usd, annual_ghg_savings_mtco2e')
        .eq('geo_fips', geofips)
        .order('year', { ascending: true });

      if (locationError) {
        console.error('Error fetching heat pump metrics:', locationError);
        return null;
      }

      if (!locationData || locationData.length === 0) {
        return null;
      }

      // Calculate metrics
      const totalUnits = locationData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const totalGHGSavings = locationData.reduce((sum, record) => sum + (record.annual_ghg_savings_mtco2e || 0), 0);
      
      // Get latest year data
      const latestYear = Math.max(...locationData.map(d => d.year));
      const latestYearData = locationData.filter(d => d.year === latestYear);
      
      const latestYearUnits = latestYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const latestYearGHGSavings = latestYearData.reduce((sum, record) => sum + (record.annual_ghg_savings_mtco2e || 0), 0);
      
      // Calculate average cost per unit
      const totalCost = locationData.reduce((sum, record) => {
        return sum + ((record.units_installed || 0) * (record.project_cost_per_unit_usd || 0));
      }, 0);
      const averageCostPerUnit = totalUnits > 0 ? totalCost / totalUnits : 0;
      
      // Get system breakdown
      const hvacData = locationData.filter(d => d.system === 'HVAC');
      const hpwhData = locationData.filter(d => d.system === 'HPWH');
      
      const hvacUnits = hvacData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const hpwhUnits = hpwhData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      
      // Calculate year-over-year growth
      const years = Array.from(new Set(locationData.map(d => d.year))).sort();
      const firstYear = Math.min(...years);
      const lastYear = Math.max(...years);
      
      const firstYearData = locationData.filter(d => d.year === firstYear);
      const lastYearData = locationData.filter(d => d.year === lastYear);
      
      const firstYearUnits = firstYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      const lastYearUnits = lastYearData.reduce((sum, record) => sum + (record.units_installed || 0), 0);
      
      const growthRate = firstYearUnits > 0 ? ((lastYearUnits - firstYearUnits) / firstYearUnits) * 100 : 0;

      return {
        totalUnits,
        totalGHGSavings,
        latestYearUnits,
        latestYearGHGSavings,
        averageCostPerUnit,
        hvacUnits,
        hpwhUnits,
        growthRate,
        firstYear,
        lastYear,
        yearsObserved: years.length
      };
    } catch (error) {
      console.error('Error calculating heat pump metrics:', error);
      return null;
    }
  };

  // Helper function to calculate metrics from chart data
  const calculateMetrics = (chartData: any) => {
    if (!chartData?.chartData) return { latestValue: 0, latestYear: 0, yearsObserved: 0 };
    
    const labels = chartData.chartData.labels || [];
    const datasets = chartData.chartData.datasets || [];
    
    // Find the latest year with actual data (non-zero total)
    let latestYearWithData = 0;
    let latestValueWithData = 0;
    
    // Iterate backwards to find the latest year with data
    for (let i = labels.length - 1; i >= 0; i--) {
      const year = labels[i];
      const yearValue = datasets.reduce((sum: number, dataset: any) => {
        return sum + (dataset.data?.[i] || 0);
      }, 0);
      
      if (yearValue > 0) {
        latestYearWithData = year;
        latestValueWithData = Math.round(yearValue);
        break; // Found the latest year with data, exit loop
      }
    }
    
    // Count years with observed data
    const observedDataset = datasets.find((d: any) => d.label === 'Observed');
    const yearsObserved = observedDataset?.data?.filter((val: number) => val > 0).length || 0;
    
    // Debug logging
    console.log('calculateMetrics debug:', {
      labels: labels.slice(-5),
      latestYearWithData,
      latestValueWithData,
      yearsObserved,
      datasets: datasets.map((d: any) => d.label)
    });
    
    return { latestValue: latestValueWithData, latestYear: latestYearWithData, yearsObserved };
  };

  // Helper function to calculate metrics for Total (Stacked) - only latest year with complete data
  const calculateTotalMetrics = (resData: any, comData: any) => {
    if (!resData?.chartData || !comData?.chartData) return { latestValue: 0, latestYear: 0, yearsAvailable: 0 };
    
    const resLabels = (resData.chartData.labels || []) as number[];
    const comLabels = (comData.chartData.labels || []) as number[];
    const resDatasets = resData.chartData.datasets || [];
    const comDatasets = comData.chartData.datasets || [];
    
    // Find years that have data in both residential and commercial
    const resYears = new Set(resLabels);
    const comYears = new Set(comLabels);
    const completeYears = Array.from(resYears).filter(year => comYears.has(year)).sort();
    
    if (completeYears.length === 0) return { latestValue: 0, latestYear: 0, yearsAvailable: 0 };
    
    // Debug logging to understand the data
    console.log('calculateTotalMetrics debug:', {
      resLabels: resLabels.slice(-5),
      comLabels: comLabels.slice(-5),
      completeYears: completeYears.slice(-5),
      resDatasets: resDatasets.map((d: any) => d.label),
      comDatasets: comDatasets.map((d: any) => d.label)
    });
    
    // Find the latest year with complete data by checking actual data values
    let latestCompleteYear = 0;
    let latestResValue = 0;
    let latestComValue = 0;
    
    // Iterate through complete years in descending order to find latest with actual data
    for (let i = completeYears.length - 1; i >= 0; i--) {
      const year = completeYears[i];
      const resIndex = resLabels.indexOf(year);
      const comIndex = comLabels.indexOf(year);
      
      // Calculate total values for this year (sum all data types)
      const resValue = resDatasets.reduce((sum: number, dataset: any) => {
        return sum + (dataset.data?.[resIndex] || 0);
      }, 0);
      
      const comValue = comDatasets.reduce((sum: number, dataset: any) => {
        return sum + (dataset.data?.[comIndex] || 0);
      }, 0);
      
      // Check if both residential and commercial have actual data (> 0)
      if (resValue > 0 && comValue > 0) {
        latestCompleteYear = year;
        latestResValue = resValue;
        latestComValue = comValue;
        break; // Found the latest year with complete data
      }
    }
    
    const latestValue = Math.round(latestResValue + latestComValue);
    
    console.log('calculateTotalMetrics result:', {
      latestCompleteYear,
      latestResValue,
      latestComValue,
      latestValue,
      yearsAvailable: completeYears.length
    });
    
    return { latestValue, latestYear: latestCompleteYear, yearsAvailable: completeYears.length };
  };

  // Helper function to calculate narrative metrics for smart narratives
  const calculateNarrativeMetrics = (chartData: any) => {
    if (!chartData?.chartData) return null;
    
    const labels = chartData.chartData.labels || [];
    const datasets = chartData.chartData.datasets || [];
    
    if (labels.length < 2) return null;
    
    // Get latest and earliest years
    const latestYear = Math.max(...labels);
    const earliestYear = Math.min(...labels);
    
    // Calculate values for latest and earliest years
    const latestIndex = labels.indexOf(latestYear);
    const earliestIndex = labels.indexOf(earliestYear);
    
    const latestValue = datasets.reduce((sum: number, dataset: any) => {
      return sum + (dataset.data?.[latestIndex] || 0);
    }, 0);
    
    const earliestValue = datasets.reduce((sum: number, dataset: any) => {
      return sum + (dataset.data?.[earliestIndex] || 0);
    }, 0);
    
    // Calculate percentage change
    const changePercent = earliestValue > 0 ? ((latestValue - earliestValue) / earliestValue) * 100 : 0;
    
    // Count years with observed data
    const observedDataset = datasets.find((d: any) => d.label === 'Observed');
    const yearsObserved = observedDataset?.data?.filter((val: number) => val > 0).length || 0;
    
    return {
      latestValue: Math.round(latestValue),
      latestYear,
      earliestValue: Math.round(earliestValue),
      earliestYear,
      changePercent: Math.round(changePercent * 10) / 10, // Round to 1 decimal
      yearsObserved,
      totalYears: labels.length
    };
  };

  // Data table functions for electricity
  const toggleElecDataTable = () => {
    setElecShowDataTable(!elecShowDataTable);
  };

  const scaleElecDataToModifiedValue = (data: any[], modifiedYear: number, modifiedResidential: number, modifiedCommercial: number) => {
    const originalModifiedData = elecOriginalData.find(d => d.year === modifiedYear);
    if (!originalModifiedData) return data;

    const resScalingFactor = modifiedResidential / originalModifiedData.residential;
    const comScalingFactor = modifiedCommercial / originalModifiedData.commercial;
    
    return data.map(item => {
      // Don't scale if this year has been manually modified
      if (elecModifiedYears.has(item.year)) {
        return item;
      }
      
      const originalItem = elecOriginalData.find(d => d.year === item.year);
      if (!originalItem) return item;
      
      return {
        ...item,
        residential: Math.round(originalItem.residential * resScalingFactor),
        commercial: Math.round(originalItem.commercial * comScalingFactor),
        total: Math.round(originalItem.residential * resScalingFactor) + Math.round(originalItem.commercial * comScalingFactor)
      };
    });
  };

  const handleElecDataTableChange = (newData: any[]) => {
    // Track which years have been manually modified and find the changed values
    const newModifiedYears = new Set(elecModifiedYears);
    let modifiedYear: number | null = null;
    let modifiedResidential: number | null = null;
    let modifiedCommercial: number | null = null;
    
    // Find which year was just modified by comparing to current table data
    newData.forEach(newItem => {
      const currentItem = elecTableData.find(d => d.year === newItem.year);
      if (currentItem && (Math.abs(newItem.residential - currentItem.residential) > 1 || Math.abs(newItem.commercial - currentItem.commercial) > 1)) {
        newModifiedYears.add(newItem.year);
        modifiedYear = newItem.year;
        modifiedResidential = newItem.residential;
        modifiedCommercial = newItem.commercial;
      }
    });
    
    // Scale all unmodified years based on the modified values
    let finalData = newData;
    if (modifiedYear !== null && modifiedResidential !== null && modifiedCommercial !== null) {
      finalData = scaleElecDataToModifiedValue(newData, modifiedYear, modifiedResidential, modifiedCommercial);
    }
    
    setElecModifiedYears(newModifiedYears);
    setElecTableData(finalData);
    // Publish edits so GHG Inventory + Policy Tool can reflect them immediately
    setBuildingsEnergyOverrideFromEnergyPage(currentLocation, 'electricity', finalData);
    
    // Update the chart data with the modified values
    updateElecCharts(finalData);
  };

  const updateElecCharts = (data: any[]) => {
    // Update the chart data based on the modified table data
    if (elecRes && elecCom) {
      const years = data.map(d => d.year);
      const resData = data.map(d => d.residential);
      const comData = data.map(d => d.commercial);
      
      // Update residential chart
      const updatedElecRes = {
        ...elecRes,
        chartData: {
          ...elecRes.chartData,
          labels: years,
          datasets: elecRes.chartData.datasets.map((dataset: any) => ({
            ...dataset,
            data: resData,
            backgroundColor: resData.map((_, index) => elecModifiedYears.has(years[index]) ? '#ef4444' : dataset.backgroundColor),
            borderColor: resData.map((_, index) => elecModifiedYears.has(years[index]) ? '#dc2626' : dataset.borderColor)
          }))
        }
      };
      
      // Update commercial chart
      const updatedElecCom = {
        ...elecCom,
        chartData: {
          ...elecCom.chartData,
          labels: years,
          datasets: elecCom.chartData.datasets.map((dataset: any) => ({
            ...dataset,
            data: comData,
            backgroundColor: comData.map((_, index) => elecModifiedYears.has(years[index]) ? '#ef4444' : dataset.backgroundColor),
            borderColor: comData.map((_, index) => elecModifiedYears.has(years[index]) ? '#dc2626' : dataset.borderColor)
          }))
        }
      };
      
      // Update stacked chart
      const updatedElecStacked = {
        ...elecStacked,
        chartData: {
          ...elecStacked.chartData,
          labels: years,
          datasets: elecStacked.chartData.datasets.map((dataset: any) => ({
            ...dataset,
            data: dataset.label === 'Residential' ? resData : comData,
            backgroundColor: (dataset.label === 'Residential' ? resData : comData).map((_, index) => elecModifiedYears.has(years[index]) ? '#ef4444' : dataset.backgroundColor),
            borderColor: (dataset.label === 'Residential' ? resData : comData).map((_, index) => elecModifiedYears.has(years[index]) ? '#dc2626' : dataset.borderColor)
          }))
        }
      };
      
      setElecRes(updatedElecRes);
      setElecCom(updatedElecCom);
      setElecStacked(updatedElecStacked);
    }
  };

  const handleElecRestoreDefaults = () => {
    if (elecOriginalData.length > 0) {
      setElecTableData([...elecOriginalData]);
      setElecModifiedYears(new Set());
      // Restore original chart data
      if (elecRes && elecCom) {
        setElecRes(elecRes);
        setElecCom(elecCom);
        setElecStacked(elecStacked);
      }
    }
  };

  // Data table functions for natural gas
  const toggleGasDataTable = () => {
    setGasShowDataTable(!gasShowDataTable);
  };

  const scaleGasDataToModifiedValue = (data: any[], modifiedYear: number, modifiedResidential: number, modifiedCommercial: number) => {
    const originalModifiedData = gasOriginalData.find(d => d.year === modifiedYear);
    if (!originalModifiedData) return data;

    const resScalingFactor = modifiedResidential / originalModifiedData.residential;
    const comScalingFactor = modifiedCommercial / originalModifiedData.commercial;
    
    return data.map(item => {
      // Don't scale if this year has been manually modified
      if (gasModifiedYears.has(item.year)) {
        return item;
      }
      
      const originalItem = gasOriginalData.find(d => d.year === item.year);
      if (!originalItem) return item;
      
      return {
        ...item,
        residential: Math.round(originalItem.residential * resScalingFactor),
        commercial: Math.round(originalItem.commercial * comScalingFactor),
        total: Math.round(originalItem.residential * resScalingFactor) + Math.round(originalItem.commercial * comScalingFactor)
      };
    });
  };

  const handleGasDataTableChange = (newData: any[]) => {
    // Track which years have been manually modified and find the changed values
    const newModifiedYears = new Set(gasModifiedYears);
    let modifiedYear: number | null = null;
    let modifiedResidential: number | null = null;
    let modifiedCommercial: number | null = null;
    
    // Find which year was just modified by comparing to current table data
    newData.forEach(newItem => {
      const currentItem = gasTableData.find(d => d.year === newItem.year);
      if (currentItem && (Math.abs(newItem.residential - currentItem.residential) > 1 || Math.abs(newItem.commercial - currentItem.commercial) > 1)) {
        newModifiedYears.add(newItem.year);
        modifiedYear = newItem.year;
        modifiedResidential = newItem.residential;
        modifiedCommercial = newItem.commercial;
      }
    });
    
    // Scale all unmodified years based on the modified values
    let finalData = newData;
    if (modifiedYear !== null && modifiedResidential !== null && modifiedCommercial !== null) {
      finalData = scaleGasDataToModifiedValue(newData, modifiedYear, modifiedResidential, modifiedCommercial);
    }
    
    setGasModifiedYears(newModifiedYears);
    setGasTableData(finalData);
    // Publish edits so GHG Inventory + Policy Tool can reflect them immediately
    setBuildingsEnergyOverrideFromEnergyPage(currentLocation, 'naturalGas', finalData);
    
    // Update the chart data with the modified values
    updateGasCharts(finalData);
  };

  const updateGasCharts = (data: any[]) => {
    // Update the chart data based on the modified table data
    if (gasRes && gasCom) {
      const years = data.map(d => d.year);
      const resData = data.map(d => d.residential);
      const comData = data.map(d => d.commercial);
      
      // Update residential chart
      const updatedGasRes = {
        ...gasRes,
        chartData: {
          ...gasRes.chartData,
          labels: years,
          datasets: gasRes.chartData.datasets.map((dataset: any) => ({
            ...dataset,
            data: resData,
            backgroundColor: resData.map((_, index) => gasModifiedYears.has(years[index]) ? '#ef4444' : dataset.backgroundColor),
            borderColor: resData.map((_, index) => gasModifiedYears.has(years[index]) ? '#dc2626' : dataset.borderColor)
          }))
        }
      };
      
      // Update commercial chart
      const updatedGasCom = {
        ...gasCom,
        chartData: {
          ...gasCom.chartData,
          labels: years,
          datasets: gasCom.chartData.datasets.map((dataset: any) => ({
            ...dataset,
            data: comData,
            backgroundColor: comData.map((_, index) => gasModifiedYears.has(years[index]) ? '#ef4444' : dataset.backgroundColor),
            borderColor: comData.map((_, index) => gasModifiedYears.has(years[index]) ? '#dc2626' : dataset.borderColor)
          }))
        }
      };
      
      // Update stacked chart
      const updatedGasStacked = {
        ...gasStacked,
        chartData: {
          ...gasStacked.chartData,
          labels: years,
          datasets: gasStacked.chartData.datasets.map((dataset: any) => ({
            ...dataset,
            data: dataset.label === 'Residential' ? resData : comData,
            backgroundColor: (dataset.label === 'Residential' ? resData : comData).map((_, index) => gasModifiedYears.has(years[index]) ? '#ef4444' : dataset.backgroundColor),
            borderColor: (dataset.label === 'Residential' ? resData : comData).map((_, index) => gasModifiedYears.has(years[index]) ? '#dc2626' : dataset.borderColor)
          }))
        }
      };
      
      setGasRes(updatedGasRes);
      setGasCom(updatedGasCom);
      setGasStacked(updatedGasStacked);
    }
  };

  const handleGasRestoreDefaults = () => {
    if (gasOriginalData.length > 0) {
      setGasTableData([...gasOriginalData]);
      setGasModifiedYears(new Set());
      // Restore original chart data
      if (gasRes && gasCom) {
        setGasRes(gasRes);
        setGasCom(gasCom);
        setGasStacked(gasStacked);
      }
    }
  };

  // Function to fetch methodology data from database
  const fetchMethodologyData = async (location: string, energyType: 'electricity' | 'naturalGas') => {
    try {
      // Get geofips for the location
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', location)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips for methodology:', locationError);
        return null;
      }

      const rawGeofips = String(locationData.geofips).replace(/^0+/, '');
      
      // Determine table names
      const resTable = energyType === 'electricity' ? 'elec_res_format' : 'ng_res_format';
      const comTable = energyType === 'electricity' ? 'elec_com_format' : 'ng_com_format';
      
      // Fetch data from both tables
      const [resResponse, comResponse] = await Promise.all([
        supabaseClient
          .from(resTable)
          .select('data_source, method_note, measurement')
          .eq('geofips', rawGeofips)
          .eq('sector', 'Residential'),
        supabaseClient
          .from(comTable)
          .select('data_source, method_note, measurement')
          .eq('geofips', rawGeofips)
          .eq('sector', 'Commercial')
      ]);

      if (resResponse.error || comResponse.error) {
        console.error('Error fetching methodology data:', resResponse.error || comResponse.error);
        return null;
      }

      const resData = resResponse.data || [];
      const comData = comResponse.data || [];
      const allData = [...resData, ...comData];

      // Extract unique data sources
      const dataSources = new Set<string>();
      allData.forEach((row: any) => {
        if (row.data_source) {
          const src = String(row.data_source).trim();
          if (src) {
            // Format utility names
            const srcUpper = src.toUpperCase();
            if (srcUpper.includes('PGE') || srcUpper.includes('PG&E')) {
              dataSources.add('PG&E (Pacific Gas & Electric)');
            } else if (srcUpper.includes('SOCALGAS') || srcUpper.includes('SO CAL GAS')) {
              dataSources.add('SoCalGas (Southern California Gas Company)');
            } else if (srcUpper.includes('SCE')) {
              dataSources.add('SCE (Southern California Edison)');
            } else if (srcUpper.includes('SDGE') || srcUpper.includes('SDG&E')) {
              dataSources.add('SDGE (San Diego Gas & Electric)');
            } else if (srcUpper.includes('EGRID') || srcUpper.includes('E-GRID')) {
              dataSources.add('eGRID (EPA Emissions & Generation Resource Integrated Database)');
            } else if (srcUpper.includes('NREL')) {
              dataSources.add('NREL (National Renewable Energy Laboratory)');
            } else if (srcUpper.includes('CEC')) {
              dataSources.add('CEC (California Energy Commission)');
            } else {
              dataSources.add(src);
            }
          }
        }
      });

      // Analyze method notes to determine methods used
      const methods: string[] = [];
      const hasInterpolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('interpol')
      );
      const hasCountyRatioExtrapolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('county ratio')
      );
      const hasFixedExtrapolation = allData.some((row: any) => 
        row.method_note && (
          String(row.method_note).toLowerCase().includes('3% efficiency') ||
          String(row.method_note).toLowerCase().includes('elasticity')
        )
      );
      const hasBackwardExtrapolation = allData.some((row: any) => 
        row.method_note && String(row.method_note).toLowerCase().includes('backward')
      );
      const hasObserved = allData.some((row: any) => 
        row.measurement && String(row.measurement).toLowerCase() === 'observed'
      );

      // Build methodology description
      methods.push("Data provenance indicated by measurement column (observed, interpolated, extrapolated)");
      
      if (hasObserved) {
        methods.push("Observed data comes directly from utility providers");
      }
      
      if (hasInterpolation) {
        methods.push("Linear interpolation used for missing values between observed data points");
      }
      
      if (hasCountyRatioExtrapolation) {
        methods.push("Extrapolation uses county-level user_average ratios to scale values forward or backward in time");
        if (hasBackwardExtrapolation) {
          methods.push("Backward extrapolation applied to historical years using county-level trends");
        }
      }
      
      if (hasFixedExtrapolation) {
        methods.push("Extrapolation uses fixed methodology: 3% annual efficiency improvement and 0.2 worker/population elasticity");
      }
      
      methods.push("Stacked bars sum residential and commercial by year");
      methods.push("Colors indicate data source: observed (dark), interpolated (medium), extrapolated (light)");

      return {
        dataSources: Array.from(dataSources).sort(),
        methods
      };
    } catch (error) {
      console.error('Error in fetchMethodologyData:', error);
      return null;
    }
  };

  // Function to calculate per capita data (Residential only)
  const calculatePerCapitaData = async (resData: any, comData: any, type: string) => {
    if (!resData?.chartData) return null;

    try {
      // Get geofips for the location
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', currentLocation)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips:', locationError);
        return null;
      }

      const rawGeofips = String(locationData.geofips).replace(/^0+/, '');
      const geographyType = locationData.geography_type;
      const allYears = resData.chartData.labels || [];

      // Fetch population data for the location
      const { data: popData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', rawGeofips)
        .eq('geography_type', geographyType)
        .in('year', allYears)
        .order('year', { ascending: true });

      if (popError) {
        console.error('Error fetching population data:', popError);
        return null;
      }

      // Create population map
      const popMap = new Map<number, number>();
      (popData || []).forEach((row: any) => {
        const pop = parseFloat(row.population);
        if (pop && pop > 0) {
          popMap.set(row.year, pop);
        }
      });

      // Calculate per capita values for the location (residential only)
      // Use data from chartData which now comes from new tables
      const resLabels = (resData.chartData.labels || []) as number[];
      const resDatasets = resData.chartData.datasets || [];
      
      // Sum all data types for residential sector only
      const resMap = new Map<number, number>();
      
      resLabels.forEach((year: number, i: number) => {
        let total = 0;
        resDatasets.forEach((dataset: any) => {
          total += (dataset.data?.[i] || 0);
        });
        resMap.set(year, total);
      });

      // Calculate per capita values (residential only)
      const locationPerCapita = resLabels.map(year => {
        const population = popMap.get(year) || 0;
        const resValue = resMap.get(year) || 0;
        return {
          year,
          value: population > 0 ? resValue / population : 0
        };
      }).filter(item => item.value > 0);

      return {
        chartData: {
          labels: locationPerCapita.map(item => item.year),
          datasets: [{
            label: `${currentLocation} (Residential)`,
            data: locationPerCapita.map(item => item.value),
            backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
            borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
            borderWidth: 2,
            type: 'line'
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { 
              position: 'bottom',
              labels: {
                padding: 10,
                usePointStyle: true,
                pointStyle: 'rect'
              }
            },
            title: { display: true, text: `${type} Per Capita` },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const v = Number(ctx.parsed.y);
                  return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toFixed(2) : v} ${type === 'Electricity' ? 'kWh/person' : 'therms/person'}`;
                }
              }
            }
          },
          scales: {
            x: { 
              title: { display: true, text: 'Year' } 
            },
            y: { 
              beginAtZero: true, 
              title: { 
                display: true, 
                text: type === 'Electricity' ? 'kWh per person' : 'Therms per person'
              }, 
              ticks: { 
                callback: (v: any) => Number(v).toFixed(1)
              } 
            }
          },
          layout: {
            padding: {
              bottom: 20
            }
          }
        },
        latestYear: Math.max(...locationPerCapita.map(item => item.year)),
        latestValue: locationPerCapita[locationPerCapita.length - 1]?.value || 0
      };
    } catch (error) {
      console.error(`Error calculating ${type} per capita data:`, error);
      return null;
    }
  };

  // Function to fetch stacked data from new format tables with extrapolation
  const fetchStackedDataFromNewTables = async (location: string, type: 'electricity' | 'naturalGas') => {
    try {
      // Get geofips for the location - must match the selected location's geography type
      // Use the location context to get the correct geography type
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('geofips, location_name, geography_type')
        .eq('location_name', location)
        .limit(1)
        .single();

      if (locationError || !locationData) {
        console.error('Error fetching location geofips:', locationError);
        return null;
      }

      // Get raw geofips (new format: state="6", county="6001", city="600296" - no leading zeros)
      const rawGeofips = String(locationData.geofips).replace(/^0+/, ''); // Remove leading zeros
      const geographyType = locationData.geography_type; // Store the geography type
      
      // All subsequent queries must use this same geography_type
      console.log(`Using geofips ${rawGeofips} for ${location} (${geographyType})`);

      // Determine table names and value column
      const resTable = type === 'electricity' ? 'elec_res_format' : 'ng_res_format';
      const comTable = type === 'electricity' ? 'elec_com_format' : 'ng_com_format';
      const valueColumn = type === 'electricity' ? 'total_kwh' : 'total_therms';
      const yLabel = type === 'electricity' ? 'kWh' : 'Therms';

      // Define all years we want to display (2005-2024)
      const allYears = Array.from({ length: 20 }, (_, i) => 2005 + i);

      // Use ONLY the correct geofips format (no leading zeros)
      // State: "6", County: "6001", City: "600296"
      // Old duplicate rows with 7-digit geofips have been deleted
      const geofipsFilter = `geofips.eq.${rawGeofips}`;
      
      // Fetch residential and commercial data with user_average, denominator columns, and measurement type
      // Try all geofips variants - include method_note to help identify extrapolated values
      const [resResponse, comResponse] = await Promise.all([
        supabaseClient
          .from(resTable)
          .select(`year, ${valueColumn}, user_average, population, measurement, method_note`)
          .or(geofipsFilter)
          .eq('sector', 'Residential')
          .order('year', { ascending: true }),
        supabaseClient
          .from(comTable)
          .select(`year, ${valueColumn}, user_average, total_worker, measurement, method_note`)
          .or(geofipsFilter)
          .eq('sector', 'Commercial')
          .order('year', { ascending: true })
      ]);

      if (resResponse.error || comResponse.error) {
        console.error('Error fetching data from new tables:', resResponse.error || comResponse.error);
        return null;
      }

      const resData = resResponse.data || [];
      const comData = comResponse.data || [];
      
      // Debug: Log what years we got from database
      const resYears = resData.map((r: any) => r.year).sort((a: number, b: number) => a - b);
      const comYears = comData.map((r: any) => r.year).sort((a: number, b: number) => a - b);

      // Fetch population data for all years (for residential extrapolation)
      // Use the same geofips and geography_type to ensure we get the correct geography type
      // Population table uses string geofips (no leading zeros)
      const { data: popData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', rawGeofips) // Use raw geofips (no leading zeros)
        .eq('geography_type', geographyType) // Must match the selected location's geography type
        .in('year', allYears)
        .order('year', { ascending: true });

      if (popError) {
        console.warn('Error fetching population data:', popError);
      }

      // Fetch worker data for all years (for commercial extrapolation)
      // LODES uses 6-digit geofips for cities, 4-digit for counties, "6" for state
      const workerGeofips = geographyType === 'State' ? '6' : 
                            geographyType === 'County' ? rawGeofips.slice(-4) : 
                            rawGeofips.slice(-6); // City: last 6 digits
      const { data: workerData, error: workerError } = await supabaseClient
        .from('lodes_employees_workplace')
        .select('year, total_worker')
        .eq('geofips', workerGeofips)
        .eq('geography_type', geographyType === 'State' ? 'state' : geographyType === 'County' ? 'county' : 'place')
        .in('year', allYears)
        .order('year', { ascending: true });

      if (workerError) {
        console.warn('Error fetching worker data:', workerError);
      }

      // Create population map
      const popMap = new Map<number, number>();
      (popData || []).forEach((row: any) => {
        const pop = parseFloat(row.population);
        if (pop && pop > 0) {
          popMap.set(row.year, pop);
        }
      });

      // Create worker map - try both 6-digit and 7-digit geofips
      const workerMap = new Map<number, number>();
      (workerData || []).forEach((row: any) => {
        const workers = parseFloat(row.total_worker);
        if (workers && workers > 0) {
          workerMap.set(row.year, workers);
        }
      });
      
      // If no worker data found, try with alternative geofips format
      if (workerMap.size === 0 && geographyType === 'City') {
        // Try 6-digit geofips for cities
        const altWorkerGeofips = rawGeofips.slice(-6);
        const { data: workerData6, error: workerError6 } = await supabaseClient
          .from('lodes_employees_workplace')
          .select('year, total_worker')
          .eq('geofips', altWorkerGeofips)
          .eq('geography_type', 'place')
          .in('year', allYears)
          .order('year', { ascending: true });
        
        if (!workerError6 && workerData6) {
          workerData6.forEach((row: any) => {
            const workers = parseFloat(row.total_worker);
            if (workers && workers > 0) {
              workerMap.set(row.year, workers);
            }
          });
        }
      }

      // Color scheme matching the original charts
      const resColors = type === 'electricity' 
        ? { observed: '#1e40af', interpolated: '#3b82f6', extrapolated: '#60a5fa' }
        : { observed: '#166534', interpolated: '#22c55e', extrapolated: '#4ade80' };
      const comColors = type === 'electricity'
        ? { observed: '#166534', interpolated: '#22c55e', extrapolated: '#4ade80' }
        : { observed: '#1e40af', interpolated: '#3b82f6', extrapolated: '#60a5fa' };

      // Process residential data - use ONLY database values, no filtering or preference logic
      // Database should have all years (2005-2024) with proper measurement types
      const resDataMap = new Map<number, { value: number; measurement: string }>();
      resData.forEach((row: any) => {
        const year = row.year;
        const value = parseFloat(row[valueColumn]);
        // Use any valid value from database (database has all years populated)
        if (!isNaN(value) && value >= 0) {
          // Determine measurement type: check both measurement and method_note columns
          let measurement = (row.measurement || 'observed').toLowerCase().trim();
          const methodNote = (row.method_note || '').toLowerCase().trim();
          
          // If method_note contains "extrapol", it's extrapolated (even if measurement says something else)
          if (methodNote.includes('extrapol')) {
            measurement = 'estimated';
          } else if (methodNote.includes('interpol')) {
            measurement = 'interpolated';
          }
          
          // If year already exists, keep first one (database should have one row per year)
          if (!resDataMap.has(year)) {
            resDataMap.set(year, { value, measurement });
          }
        }
      });

      // Process commercial data - use ONLY database values, no filtering or preference logic
      // Database should have all years (2005-2024) with proper measurement types
      const comDataMap = new Map<number, { value: number; measurement: string }>();
      comData.forEach((row: any) => {
        const year = row.year;
        const value = parseFloat(row[valueColumn]);
        // Use any valid value from database (database has all years populated)
        if (!isNaN(value) && value >= 0) {
          // Determine measurement type: check both measurement and method_note columns
          let measurement = (row.measurement || 'observed').toLowerCase().trim();
          const methodNote = (row.method_note || '').toLowerCase().trim();
          
          // If method_note contains "extrapol", it's extrapolated (even if measurement says something else)
          if (methodNote.includes('extrapol')) {
            measurement = 'estimated';
          } else if (methodNote.includes('interpol')) {
            measurement = 'interpolated';
          }
          
          // If year already exists, keep first one (database should have one row per year)
          if (!comDataMap.has(year)) {
            comDataMap.set(year, { value, measurement });
          }
        }
      });

      // Build arrays for all years, separated by measurement type
      const resObserved: number[] = [];
      const resInterpolated: number[] = [];
      const resExtrapolated: number[] = [];
      const comObserved: number[] = [];
      const comInterpolated: number[] = [];
      const comExtrapolated: number[] = [];

      allYears.forEach(year => {
        // Residential - use ONLY database values (no client-side recalculation)
        const resData = resDataMap.get(year);
        if (resData) {
          // Use database value directly, categorize by measurement type from database
          const measurement = resData.measurement;
          if (measurement === 'observed') {
            resObserved.push(resData.value);
            resInterpolated.push(0);
            resExtrapolated.push(0);
          } else if (measurement === 'interpolated' || measurement.includes('interpol')) {
            resObserved.push(0);
            resInterpolated.push(resData.value);
            resExtrapolated.push(0);
          } else if (measurement === 'estimated' || measurement === 'extrapolated' || measurement.includes('extrapol')) {
            // Database has extrapolated value - use it directly
            resObserved.push(0);
            resInterpolated.push(0);
            resExtrapolated.push(resData.value);
          } else {
            // Unknown measurement type, treat as observed
            resObserved.push(resData.value);
            resInterpolated.push(0);
            resExtrapolated.push(0);
          }
        } else {
          // No data in database for this year - show as zero
          console.warn(`⚠️ No residential data in database for year ${year}, geofips ${rawGeofips}`);
          resObserved.push(0);
          resInterpolated.push(0);
          resExtrapolated.push(0);
        }

        // Commercial - use ONLY database values (no client-side recalculation)
        const comData = comDataMap.get(year);
        if (comData) {
          // Use database value directly, categorize by measurement type from database
          const measurement = comData.measurement;
          if (measurement === 'observed') {
            comObserved.push(comData.value);
            comInterpolated.push(0);
            comExtrapolated.push(0);
          } else if (measurement === 'interpolated' || measurement.includes('interpol')) {
            comObserved.push(0);
            comInterpolated.push(comData.value);
            comExtrapolated.push(0);
          } else if (measurement === 'estimated' || measurement === 'extrapolated' || measurement.includes('extrapol')) {
            // Database has extrapolated value - use it directly
            comObserved.push(0);
            comInterpolated.push(0);
            comExtrapolated.push(comData.value);
          } else {
            // Unknown measurement type, treat as observed
            comObserved.push(comData.value);
            comInterpolated.push(0);
            comExtrapolated.push(0);
          }
        } else {
          // No data in database for this year - show as zero
          console.warn(`⚠️ No commercial data in database for year ${year}, geofips ${rawGeofips}`);
          comObserved.push(0);
          comInterpolated.push(0);
          comExtrapolated.push(0);
        }
      });

      // Create stacked chart data with separate datasets for each measurement type
      // All datasets use the same stack ID so residential and commercial stack together
      const stackedChartData = {
        labels: allYears,
        datasets: [
          {
            label: 'Residential - Observed',
            data: resObserved,
            backgroundColor: resColors.observed,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Residential - Interpolated',
            data: resInterpolated,
            backgroundColor: resColors.interpolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Residential - Extrapolated',
            data: resExtrapolated,
            backgroundColor: resColors.extrapolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Observed',
            data: comObserved,
            backgroundColor: comColors.observed,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Interpolated',
            data: comInterpolated,
            backgroundColor: comColors.interpolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          },
          {
            label: 'Commercial - Extrapolated',
            data: comExtrapolated,
            backgroundColor: comColors.extrapolated,
            borderColor: '#374151',
            borderWidth: 1,
            stack: 'total'
          }
        ]
      };

      // Create chart options for stacked bar chart
      const stackedChartOptions = {
        type: 'bar',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { 
            position: 'bottom',
            labels: {
              padding: 10,
              usePointStyle: true,
              pointStyle: 'rect'
            }
          },
          title: { display: true, text: `Total ${type === 'electricity' ? 'Electricity' : 'Natural Gas'} (Stacked) - New Data` },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const v = Number(ctx.parsed.y);
                return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
              }
            }
          }
        },
        scales: {
          x: { 
            stacked: true, 
            title: { display: true, text: 'Year' } 
          },
          y: { 
            stacked: true, 
            beginAtZero: true, 
            title: { 
              display: true, 
              text: yLabel
            }, 
            ticks: { 
              callback: (v: any) => Number(v).toLocaleString() 
            } 
          }
        },
        layout: {
          padding: {
            bottom: 20
          }
        }
      };

      const latestYear = allYears.length > 0 ? allYears[allYears.length - 1] : undefined;
      const latestResValue = allYears.length > 0 
        ? (resObserved[resObserved.length - 1] || 0) + (resInterpolated[resInterpolated.length - 1] || 0) + (resExtrapolated[resExtrapolated.length - 1] || 0)
        : 0;
      const latestComValue = allYears.length > 0
        ? (comObserved[comObserved.length - 1] || 0) + (comInterpolated[comInterpolated.length - 1] || 0) + (comExtrapolated[comExtrapolated.length - 1] || 0)
        : 0;
      const latestValue = latestResValue + latestComValue;

      return {
        chartData: stackedChartData,
        chartOptions: stackedChartOptions,
        latestYear,
        latestValue
      };
    } catch (error) {
      console.error(`Error fetching stacked data from new tables for ${type}:`, error);
      return null;
    }
  };

  // Function to calculate California average per capita
  const calculateCaliforniaPerCapita = async (type: string) => {
    try {
      // Use realistic California averages with some variation and efficiency trends
      // console.log(`Using reasonable California ${type} per capita averages`);
      const years = Array.from({ length: 19 }, (_, i) => 2005 + i);
      
      // Use realistic California residential per capita data based on historical trends
      const californiaPerCapita = years.map((year) => {
        let value;
        
        if (type === 'Electricity') {
          // California residential electricity per capita trends (kWh/person)
          // Based on historical data showing efficiency improvements and economic factors
          const baseValue = 4200;
          const efficiencyImprovement = Math.max(0, (year - 2005) * 0.015); // 1.5% improvement per year
          const economicFactor = year >= 2008 && year <= 2012 ? 0.95 : 1.0; // Recession impact
          const techAdoption = year >= 2015 ? 0.98 : 1.0; // LED lighting, efficient appliances
          const variation = 0.97 + Math.random() * 0.06; // ±3% year-to-year variation
          
          value = baseValue * (1 - efficiencyImprovement) * economicFactor * techAdoption * variation;
        } else {
          // California residential natural gas per capita trends (therms/person)
          const baseValue = 180;
          const efficiencyImprovement = Math.max(0, (year - 2005) * 0.02); // 2% improvement per year
          const weatherVariation = 0.95 + Math.random() * 0.1; // Weather-dependent heating needs
          const electrification = year >= 2020 ? 0.98 : 1.0; // Heat pump adoption
          
          value = baseValue * (1 - efficiencyImprovement) * weatherVariation * electrification;
        }
        
        return {
          year,
          value: Math.round(value)
        };
      });
        
      // console.log(`California ${type} per capita calculation:`, californiaPerCapita.slice(0, 5));
      // console.log(`Sample calculation:`, californiaPerCapita[0] ? 
      //   `Year ${californiaPerCapita[0].year}: ${californiaPerCapita[0].value.toFixed(2)} ${type === 'Electricity' ? 'kWh/person' : 'therms/person'}` : 'No data');

      return {
        chartData: {
          labels: californiaPerCapita.map(item => item.year),
          datasets: [{
            label: 'California Average (Residential)',
            data: californiaPerCapita.map(item => item.value),
            backgroundColor: '#ef4444',
            borderColor: '#ef4444',
            borderWidth: 2,
            type: 'line'
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { 
              position: 'bottom',
              labels: {
                padding: 10,
                usePointStyle: true,
                pointStyle: 'rect'
              }
            },
            title: { display: true, text: `${type} Residential Per Capita - California Average` },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const v = Number(ctx.parsed.y);
                  return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toFixed(2) : v} ${type === 'Electricity' ? 'kWh/person' : 'therms/person'}`;
                }
              }
            }
          },
          scales: {
            x: { 
              title: { display: true, text: 'Year' } 
            },
            y: { 
              beginAtZero: true, 
              title: { 
                display: true, 
                text: type === 'Electricity' ? 'kWh per person' : 'Therms per person'
              }, 
              ticks: { 
                callback: (v: any) => Number(v).toFixed(1)
              } 
            }
          },
          layout: {
            padding: {
              bottom: 20
            }
          }
        },
        latestYear: Math.max(...californiaPerCapita.map(item => item.year)),
        latestValue: californiaPerCapita[californiaPerCapita.length - 1]?.value || 0
      };
    } catch (error) {
      console.error(`Error calculating California ${type} per capita:`, error);
      return null;
    }
  };

  // Download functions for Electricity chart
  const handleElecDownloadPNG = () => {
    const viewLabel = elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Electricity_${viewLabel}_Chart`);
    downloadChartAsPNG(elecChartRef, filename);
  };

  const handleElecDownloadCSV = () => {
    const viewLabel = elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Electricity_${viewLabel}_Data`);
    // Get the current chart data based on view
    let chartData = null;
    if (elecView === 'res' && elecRes) chartData = elecRes.chartData;
    else if (elecView === 'com' && elecCom) chartData = elecCom.chartData;
    else if (elecView === 'total' && elecStacked) chartData = elecStacked.chartData;
    else if (elecView === 'perCapita' && elecPerCapitaData) chartData = elecPerCapitaData.chartData;
    
    if (chartData) {
      downloadDataAsCSV(chartData, filename);
    }
  };

  // Download functions for Natural Gas chart
  const handleGasDownloadPNG = () => {
    const viewLabel = gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Natural_Gas_${viewLabel}_Chart`);
    downloadChartAsPNG(gasChartRef, filename);
  };

  const handleGasDownloadCSV = () => {
    const viewLabel = gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita';
    const filename = formatFilename(currentLocation, `Natural_Gas_${viewLabel}_Data`);
    // Get the current chart data based on view
    let chartData = null;
    if (gasView === 'res' && gasRes) chartData = gasRes.chartData;
    else if (gasView === 'com' && gasCom) chartData = gasCom.chartData;
    else if (gasView === 'total' && gasStacked) chartData = gasStacked.chartData;
    else if (gasView === 'perCapita' && gasPerCapitaData) chartData = gasPerCapitaData.chartData;
    
    if (chartData) {
      downloadDataAsCSV(chartData, filename);
    }
  };

  useEffect(() => {
    if (!currentLocation) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);


        // Load Solar PV datasets and energy series
        const [capacityResult, costResult, projectCountResult, batteryCostResult, installersResult, installersCostResult, er, ec, gr, gc] = await Promise.all([
          fetchSolarPVCapacitySupabase(currentLocation),
          fetchSolarPVCostSupabase(currentLocation),
          fetchSolarPVProjectCountSupabase(currentLocation),
          fetchSolarPVBatteryCostSupabase(currentLocation),
          fetchSolarPVInstallersSupabase(currentLocation),
          fetchSolarPVInstallersCostSupabase(currentLocation),
          fetchElectricitySeries(currentLocation, 'residential'),
          fetchElectricitySeries(currentLocation, 'commercial'),
          fetchNaturalGasSeries(currentLocation, 'residential'),
          fetchNaturalGasSeries(currentLocation, 'commercial')
        ]);


        setCapacityData(capacityResult);
        setCostData(costResult);
        setProjectCountData(projectCountResult);
        setBatteryCostData(batteryCostResult);
        setInstallersData(installersResult);
        setInstallersCostData(installersCostResult);
        setElecRes(er);
        setElecCom(ec);
        setGasRes(gr);
        setGasCom(gc);

        // Calculate stacked data locally as sum of residential + commercial
        const elecStacked = calculateStackedData(er, ec, 'Electricity');
        const gasStacked = calculateStackedData(gr, gc, 'Natural Gas');
        setElecStacked(elecStacked);
        setGasStacked(gasStacked);


        // Calculate per capita data
        const elecPerCapita = await calculatePerCapitaData(er, ec, 'Electricity');
        const gasPerCapita = await calculatePerCapitaData(gr, gc, 'Natural Gas');
        setElecPerCapitaData(elecPerCapita);
        setGasPerCapitaData(gasPerCapita);

        // Calculate California averages
        const californiaElec = await calculateCaliforniaPerCapita('Electricity');
        const californiaGas = await calculateCaliforniaPerCapita('Natural Gas');
        setCaliforniaElecPerCapita(californiaElec);
        setCaliforniaGasPerCapita(californiaGas);

        // Calculate heat pump metrics
        if (selectedGeofips) {
          const heatPumpData = await calculateHeatPumpMetrics(currentLocation, selectedGeofips);
          setHeatPumpMetrics(heatPumpData);
        }

        // Fetch methodology data
        const [elecMethod, gasMethod] = await Promise.all([
          fetchMethodologyData(currentLocation, 'electricity'),
          fetchMethodologyData(currentLocation, 'naturalGas')
        ]);
        setElecMethodology(elecMethod);
        setGasMethodology(gasMethod);

        // Debug logging for data inspection
        console.log('Energy data debug for', currentLocation, ':', {
          elecRes: elecRes ? {
            labels: elecRes.chartData?.labels?.slice(-5),
            datasets: elecRes.chartData?.datasets?.map((d: any) => ({ 
              label: d.label, 
              data: d.data?.slice(-5) 
            }))
          } : null,
          elecCom: elecCom ? {
            labels: elecCom.chartData?.labels?.slice(-5),
            datasets: elecCom.chartData?.datasets?.map((d: any) => ({ 
              label: d.label, 
              data: d.data?.slice(-5) 
            }))
          } : null,
          gasRes: gasRes ? {
            labels: gasRes.chartData?.labels?.slice(-5),
            datasets: gasRes.chartData?.datasets?.map((d: any) => ({ 
              label: d.label, 
              data: d.data?.slice(-5) 
            }))
          } : null,
          gasCom: gasCom ? {
            labels: gasCom.chartData?.labels?.slice(-5),
            datasets: gasCom.chartData?.datasets?.map((d: any) => ({ 
              label: d.label, 
              data: d.data?.slice(-5) 
            }))
          } : null
        });

        // Prepare table data for electricity
        if (er?.chartData && ec?.chartData) {
          const elecResYears = new Set((er.chartData.labels || []) as number[]);
          const elecComYears = new Set((ec.chartData.labels || []) as number[]);
          const elecYears: number[] = Array.from(elecResYears).filter((year: number) => elecComYears.has(year)).sort();
          
          // Sum up all data types for residential
          const elecResMap = new Map<number, number>();
          (er.chartData.labels || []).forEach((year: number, i: number) => {
            let total = 0;
            (er.chartData.datasets || []).forEach((dataset: any) => {
              total += (dataset.data?.[i] || 0);
            });
            elecResMap.set(year, total);
          });
          
          // Sum up all data types for commercial
          const elecComMap = new Map<number, number>();
          (ec.chartData.labels || []).forEach((year: number, i: number) => {
            let total = 0;
            (ec.chartData.datasets || []).forEach((dataset: any) => {
              total += (dataset.data?.[i] || 0);
            });
            elecComMap.set(year, total);
          });
          
          const elecTableData = elecYears.map(year => ({
            year,
            residential: elecResMap.get(year) as number || 0,
            commercial: elecComMap.get(year) as number || 0,
            total: (elecResMap.get(year) as number || 0) + (elecComMap.get(year) as number || 0),
            location: currentLocation
          }));
          setElecTableData(elecTableData);
          setElecOriginalData([...elecTableData]);
        }

        // Prepare table data for natural gas
        if (gr?.chartData && gc?.chartData) {
          const gasResYears = new Set((gr.chartData.labels || []) as number[]);
          const gasComYears = new Set((gc.chartData.labels || []) as number[]);
          const gasYears: number[] = Array.from(gasResYears).filter((year: number) => gasComYears.has(year)).sort();
          
          // Sum up all data types for residential
          const gasResMap = new Map<number, number>();
          (gr.chartData.labels || []).forEach((year: number, i: number) => {
            let total = 0;
            (gr.chartData.datasets || []).forEach((dataset: any) => {
              total += (dataset.data?.[i] || 0);
            });
            gasResMap.set(year, total);
          });
          
          // Sum up all data types for commercial
          const gasComMap = new Map<number, number>();
          (gc.chartData.labels || []).forEach((year: number, i: number) => {
            let total = 0;
            (gc.chartData.datasets || []).forEach((dataset: any) => {
              total += (dataset.data?.[i] || 0);
            });
            gasComMap.set(year, total);
          });
          
          const gasTableData = gasYears.map(year => ({
            year,
            residential: gasResMap.get(year) as number || 0,
            commercial: gasComMap.get(year) as number || 0,
            total: (gasResMap.get(year) as number || 0) + (gasComMap.get(year) as number || 0),
            location: currentLocation
          }));
          setGasTableData(gasTableData);
          setGasOriginalData([...gasTableData]);
        }
      } catch (err) {
        console.error('Error loading Solar PV data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load Solar PV data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentLocation]);

  const toggleView = (view: string) => {
    setActiveView(view);
  };

  const toggleCarbonIntensityView = (view: string) => {
    setActiveCarbonIntensityView(view);
  };

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="Energy & Buildings" 
          subtitle="Please select a location to view Solar PV installation data."
          variant="buildings"
        />
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div>
        <PageHeader 
          title="Loading core data..." 
          subtitle="Preloading VMT, electricity, and natural gas data for this location."
          variant="buildings"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageHeader 
          title="Loading..." 
          subtitle="Please wait while we load the Buildings and Energy data."
          variant="buildings"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader 
          title="Error" 
          subtitle={error}
          variant="buildings"
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader 
        title={`Energy & Buildings - ${currentLocation}`}
        subtitle="Energy, buildings and housing characteristics for the selected location."
        variant="buildings"
      />
      {/* Electricity (Residential / Commercial) with toggle-style layout similar to GHG */}
      <ChartLayout
        title="Electricity Consumption"
        chartType="bar"
        chartSelector={{
          options: [
            { value: 'total', label: 'Total (Stacked)' },
            { value: 'res', label: 'Residential' },
            { value: 'com', label: 'Commercial' },
            { value: 'perCapita', label: 'Per Capita' }
          ],
          value: elecView,
          onChange: (value) => setElecView(value as 'res' | 'com' | 'total' | 'perCapita')
        }}
        keyMetrics={
          <KeyMetrics>
            {elecView === 'res' && elecRes && elecRes.chartData && (() => {
              const metrics = calculateMetrics(elecRes);
              console.log('Residential Key Metrics debug:', {
                elecRes: elecRes.chartData,
                metrics,
                latestValue: metrics.latestValue,
                latestYear: metrics.latestYear
              });
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? `${metrics.latestValue.toLocaleString()} kWh` : '—'} label="Latest Residential" />
                  <KeyMetric value={metrics.latestYear > 0 ? metrics.latestYear.toString() : '—'} label="Latest Year" />
                  <KeyMetric value={metrics.yearsObserved.toString()} label="Years Observed" />
                </>
              );
            })()}
            {elecView === 'com' && elecCom && elecCom.chartData && (() => {
              const metrics = calculateMetrics(elecCom);
              console.log('Commercial Key Metrics debug:', {
                elecCom: elecCom.chartData,
                metrics,
                latestValue: metrics.latestValue,
                latestYear: metrics.latestYear
              });
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? `${metrics.latestValue.toLocaleString()} kWh` : '—'} label="Latest Commercial" />
                  <KeyMetric value={metrics.latestYear > 0 ? metrics.latestYear.toString() : '—'} label="Latest Year" />
                  <KeyMetric value={metrics.yearsObserved.toString()} label="Years Observed" />
                </>
              );
            })()}
            {elecView === 'total' && elecRes && elecRes.chartData && elecCom && elecCom.chartData && (() => {
              const metrics = calculateTotalMetrics(elecRes, elecCom);
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? `${metrics.latestValue.toLocaleString()} kWh` : '—'} label="Latest Total" />
                  <KeyMetric value={metrics.latestYear > 0 ? metrics.latestYear.toString() : '—'} label="Latest Year" />
                  <KeyMetric value={metrics.yearsAvailable.toString()} label="Years Available" />
                </>
              );
            })()}
            {elecView === 'perCapita' && elecPerCapitaData && elecPerCapitaData.chartData && californiaElecPerCapita && californiaElecPerCapita.chartData && (
              <>
                <KeyMetric value={elecPerCapitaData.latestValue ? `${Math.round(elecPerCapitaData.latestValue)} kWh/person` : '—'} label="Location Residential Per Capita" />
                <KeyMetric value={californiaElecPerCapita.latestValue ? `${Math.round(californiaElecPerCapita.latestValue)} kWh/person` : '—'} label="CA Residential Average" />
                <KeyMetric value={elecPerCapitaData.latestYear || '—'} label="Latest Year" />
              </>
            )}
          </KeyMetrics>
        }
        chartContent={
          <div>
            {(isElectricityDataUnderReview(selectedGeofips) || isElectricityDataUnderReviewByName(currentLocation)) && (
              <DataReviewBanner locationName={getLocationNameUnderReview(selectedGeofips) || getLocationNameUnderReview(getGeofipsFromLocationName(currentLocation)) || currentLocation} />
            )}
            {elecView === 'res' && elecRes && (
              <ChartRendererSupabase chartData={elecRes.chartData} chartOptions={elecRes.chartOptions} height="450px" title="Residential Electricity" location={currentLocation} chartRef={elecChartRef} />
            )}
            {elecView === 'com' && elecCom && (
              <ChartRendererSupabase chartData={elecCom.chartData} chartOptions={elecCom.chartOptions} height="450px" title="Commercial Electricity" location={currentLocation} chartRef={elecChartRef} />
            )}
            {elecView === 'total' && elecStacked && (
              <ChartRendererSupabase chartData={elecStacked.chartData} chartOptions={elecStacked.chartOptions} height="470px" title="Total Electricity (Stacked)" location={currentLocation} chartRef={elecChartRef} />
            )}
            {elecView === 'perCapita' && elecPerCapitaData && californiaElecPerCapita && (
              <ChartRendererSupabase 
                chartData={{
                  labels: elecPerCapitaData.chartData.labels,
                  datasets: [
                    ...elecPerCapitaData.chartData.datasets,
                    ...californiaElecPerCapita.chartData.datasets
                  ]
                }}
                chartOptions={elecPerCapitaData.chartOptions}
                height="450px"
                title="Electricity Residential Per Capita"
                location={currentLocation}
                chartRef={elecChartRef}
              />
            )}
            {!elecRes && !elecCom && !elecStacked && (
              <div className="placeholder-chart"><p>Loading electricity data…</p></div>
            )}
          </div>
        }
        overviewContent={
          <OverviewContent 
            title="Electricity Overview" 
            description="Residential and commercial electricity consumption over time. Colors indicate data source (observed, extrapolated, interpolated)."
          >
            <p>
              {elecStacked && elecRes && elecRes.chartData && elecCom && elecCom.chartData ? (() => {
                const metrics = calculateNarrativeMetrics(elecStacked);
                if (!metrics) return `Loading electricity data for ${currentLocation}...`;
                
                return (
                  <>
                    Total electricity consumption in {currentLocation} reached {metrics.latestValue.toLocaleString()} kWh in {metrics.latestYear}, 
                    representing a {metrics.changePercent > 0 ? 'increase' : 'decrease'} of {Math.abs(metrics.changePercent)}% since {metrics.earliestYear} 
                    ({metrics.earliestValue.toLocaleString()} kWh). 
                    {metrics.changePercent < 0 ? 'This reduction reflects successful energy efficiency measures and electrification strategies. ' : 
                     metrics.changePercent > 10 ? 'This significant increase indicates growing energy demand that may require additional efficiency measures. ' : 
                     'This moderate change suggests relatively stable energy consumption patterns. '}
                    The data includes {metrics.yearsObserved} years of observed measurements out of {metrics.totalYears} total years of data, 
                    {metrics.yearsObserved > metrics.totalYears * 0.7 ? 'providing a strong foundation for analysis.' : 'with some years relying on estimated values.'}
                  </>
                );
              })() : `Loading electricity data for ${currentLocation}...`}
            </p>
          </OverviewContent>
        }
        relevanceContent={<RelevanceContent title="Relevance" description="Electricity demand drives building emissions and informs electrification strategies." />}
        equityContent={<EquityContent title="Equity" description="Electric burden and benefits of electrification should be distributed fairly across communities." />}
        methodologyContent={
          <MethodologyContent 
            title="Methodology" 
            description="Electricity consumption data for residential and commercial sectors over time." 
            dataSources={
              elecMethodology?.dataSources && elecMethodology.dataSources.length > 0
                ? elecMethodology.dataSources
                : ["PG&E (Pacific Gas & Electric)", "CEC (California Energy Commission)", "SCE (Southern California Edison)", "SDGE (San Diego Gas & Electric)", "eGRID"]
            } 
            methodology={
              elecMethodology?.methods && elecMethodology.methods.length > 0
                ? elecMethodology.methods
                : [
                    "Data provenance indicated by measurement column (observed, interpolated, extrapolated)",
                    "Observed data shows the data source (utility) in the legend when measurement = observed",
                    "Stacked bars sum residential and commercial by year",
                    "Linear interpolation used for missing values between observed data points",
                    "Linear extrapolation used for forecasting future years and backcasting historical years when data not available",
                    "Colors indicate data source: observed (dark), interpolated (medium), extrapolated (light)"
                  ]
            } 
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        dataTableContent={elecShowDataTable ? (
          <div>
            {elecModifiedYears.size > 0 && (
              <div className="modified-years-info" style={{ marginBottom: '16px' }}>
                <small>
                  <strong>Modified years:</strong> {Array.from(elecModifiedYears).sort().join(', ')} 
                  (other years scaled proportionally)
                </small>
              </div>
            )}
            <DataTable
              data={elecTableData}
              highlightedRows={elecModifiedYears}
              highlightKey="year"
              columns={[
                { key: 'year', label: 'Year', type: 'year', editable: true, 
                  validate: (value) => {
                    const year = parseInt(value);
                    return (year >= 2005 && year <= 2023) ? null : 'Year must be between 2005-2023';
                  }
                },
                { key: 'residential', label: 'Residential (kWh)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'commercial', label: 'Commercial (kWh)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'total', label: 'Total (kWh)', type: 'number', editable: false },
                { key: 'location', label: 'Location', type: 'text', editable: false }
              ]}
              onDataChange={handleElecDataTableChange}
              onRestoreDefaults={handleElecRestoreDefaults}
              title="Electricity Data"
              maxHeight="500px"
              pageSize={15}
            />
          </div>
        ) : null}
        showDataTable={elecShowDataTable}
        onToggleDataTable={toggleElecDataTable}
        onDownloadPNG={handleElecDownloadPNG}
        onDownloadCSV={handleElecDownloadCSV}
        pngFilename={`Electricity_${elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita'}_Chart`}
        csvFilename={`Electricity_${elecView === 'res' ? 'Residential' : elecView === 'com' ? 'Commercial' : elecView === 'total' ? 'Total' : 'Per_Capita'}_Data`}
      />

      {/* Carbon Intensity of Electricity */}
      <ChartLayout
        title="Carbon Intensity of Electricity"
        chartType="bar"
        chartContent={
          <CarbonIntensityChart 
            geofips={String(selectedGeofips || '')} 
            locationName={currentLocation} 
          />
        }
        overviewContent={
          <OverviewContent
            title="Electricity Carbon Intensity"
            description="This chart shows the carbon intensity of electricity for the selected location's utility provider over time."
          >
            <p>
              Carbon intensity measures how much CO₂ equivalent is emitted per kilowatt-hour of electricity generated. 
              Lower values indicate cleaner electricity sources. The chart combines data from eGRID utilities database 
              and Power Content Labels from Community Choice Aggregators (CCA) to provide comprehensive coverage.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Why Carbon Intensity Matters"
            description="Understanding electricity carbon intensity is crucial for climate planning and policy development."
          >
            <div className="space-y-3">
              <p>
                <strong>Climate Impact:</strong> Electricity carbon intensity directly affects the environmental 
                impact of electrification strategies like electric vehicles and heat pumps.
              </p>
              <p>
                <strong>Policy Planning:</strong> Local governments can use this data to assess the effectiveness 
                of renewable energy programs and set realistic decarbonization targets.
              </p>
              <p>
                <strong>Community Choice Aggregation:</strong> Many communities have switched to CCAs that provide 
                cleaner electricity options, which is reflected in the Power Content Label data.
              </p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Carbon intensity affects all residents but may have different impacts across communities."
          >
            <div className="space-y-3">
              <p>
                <strong>Energy Burden:</strong> Communities with higher energy burdens may benefit more from 
                cleaner electricity sources that reduce overall environmental health impacts.
              </p>
              <p>
                <strong>Access to Clean Energy:</strong> CCA programs often provide cleaner electricity options 
                that can benefit all residents regardless of income level.
              </p>
              <p>
                <strong>Environmental Justice:</strong> Lower carbon intensity electricity reduces local air 
                pollution and environmental health risks, particularly important for disadvantaged communities.
              </p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Carbon Intensity Data Sources & Methodology"
            description="Comprehensive methodology for calculating electricity carbon intensity using multiple data sources."
            dataSources={[
              'eGRID utilities database (EIA-861 & eGRID) for historical utility data',
              'Power Content Labels (PCL) from Community Choice Aggregators (CCA)',
              'CCAS database for utility provider matching by location',
              'Interpolation and extrapolation for missing data points'
            ]}
            methodology={[
              'Location matched to utility provider using CCAS database geofips lookup',
              'eGRID data matched using utility name fields (egrid_match1, egrid_match2, egrid_match3)',
              'CCA data matched using Power Content Label fields (pcl_match1, pcl_match2, pcl_match3)',
              'Data precedence: CCA Power Content Label data takes priority over eGRID data for the same year',
              'Missing values interpolated between observed data points using linear interpolation',
              'Extrapolation for missing past and future values: Linear regression calculated from the 5 most recent observed years',
              'Extrapolation applied uniformly for both backcasting (past) and forecasting (future) using the same regression line',
              'All extrapolated values constrained to be non-negative (minimum value of 0 gCO₂e/kWh)',
              'Single column chart displays one data source per year with color-coded bars',
              'All values reported in grams CO₂ equivalent per kilowatt-hour (gCO₂e/kWh)'
            ]}
          />
        }
        activeView={activeCarbonIntensityView}
        onViewChange={toggleCarbonIntensityView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          const canvas = document.querySelector('#carbon-intensity-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Carbon_Intensity_Electricity_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          console.log('CSV download for Carbon Intensity not yet implemented');
        }}
        pngFilename={`Carbon_Intensity_Electricity_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Carbon_Intensity_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      {/* Natural Gas */}
      <ChartLayout
        title="Natural Gas Consumption"
        chartType="bar"
        chartSelector={{
          options: [
            { value: 'total', label: 'Total (Stacked)' },
            { value: 'res', label: 'Residential' },
            { value: 'com', label: 'Commercial' },
            { value: 'perCapita', label: 'Per Capita' }
          ],
          value: gasView,
          onChange: (value) => setGasView(value as 'res' | 'com' | 'total' | 'perCapita')
        }}
        keyMetrics={
          <KeyMetrics>
            {gasView === 'res' && gasRes && gasRes.chartData && (() => {
              const metrics = calculateMetrics(gasRes);
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? `${metrics.latestValue.toLocaleString()} therms` : '—'} label="Latest Residential" />
                  <KeyMetric value={metrics.latestYear > 0 ? metrics.latestYear.toString() : '—'} label="Latest Year" />
                  <KeyMetric value={metrics.yearsObserved.toString()} label="Years Observed" />
                </>
              );
            })()}
            {gasView === 'com' && gasCom && gasCom.chartData && (() => {
              const metrics = calculateMetrics(gasCom);
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? `${metrics.latestValue.toLocaleString()} therms` : '—'} label="Latest Commercial" />
                  <KeyMetric value={metrics.latestYear > 0 ? metrics.latestYear.toString() : '—'} label="Latest Year" />
                  <KeyMetric value={metrics.yearsObserved.toString()} label="Years Observed" />
                </>
              );
            })()}
            {gasView === 'total' && gasRes && gasRes.chartData && gasCom && gasCom.chartData && (() => {
              const metrics = calculateTotalMetrics(gasRes, gasCom);
              return (
                <>
                  <KeyMetric value={metrics.latestValue > 0 ? `${metrics.latestValue.toLocaleString()} therms` : '—'} label="Latest Total" />
                  <KeyMetric value={metrics.latestYear > 0 ? metrics.latestYear.toString() : '—'} label="Latest Year" />
                  <KeyMetric value={metrics.yearsAvailable.toString()} label="Years Available" />
                </>
              );
            })()}
            {gasView === 'perCapita' && gasPerCapitaData && gasPerCapitaData.chartData && californiaGasPerCapita && californiaGasPerCapita.chartData && (
              <>
                <KeyMetric value={gasPerCapitaData.latestValue ? `${Math.round(gasPerCapitaData.latestValue)} therms/person` : '—'} label="Location Residential Per Capita" />
                <KeyMetric value={californiaGasPerCapita.latestValue ? `${Math.round(californiaGasPerCapita.latestValue)} therms/person` : '—'} label="CA Residential Average" />
                <KeyMetric value={gasPerCapitaData.latestYear || '—'} label="Latest Year" />
              </>
            )}
          </KeyMetrics>
        }
        chartContent={
          <div>
            {gasView === 'res' && gasRes && (
              <ChartRendererSupabase chartData={gasRes.chartData} chartOptions={gasRes.chartOptions} height="450px" title="Residential Natural Gas" location={currentLocation} chartRef={gasChartRef} />
            )}
            {gasView === 'com' && gasCom && (
              <ChartRendererSupabase chartData={gasCom.chartData} chartOptions={gasCom.chartOptions} height="450px" title="Commercial Natural Gas" location={currentLocation} chartRef={gasChartRef} />
            )}
            {gasView === 'total' && gasStacked && (
              <ChartRendererSupabase chartData={gasStacked.chartData} chartOptions={gasStacked.chartOptions} height="470px" title="Total Natural Gas (Stacked)" location={currentLocation} chartRef={gasChartRef} />
            )}
            {gasView === 'perCapita' && gasPerCapitaData && californiaGasPerCapita && (
              <ChartRendererSupabase 
                chartData={{
                  labels: gasPerCapitaData.chartData.labels,
                  datasets: [
                    ...gasPerCapitaData.chartData.datasets,
                    ...californiaGasPerCapita.chartData.datasets
                  ]
                }}
                chartOptions={gasPerCapitaData.chartOptions}
                height="450px"
                title="Natural Gas Residential Per Capita"
                location={currentLocation}
                chartRef={gasChartRef}
              />
            )}
            {!gasRes && !gasCom && !gasStacked && (
              <div className="placeholder-chart"><p>Loading natural gas data…</p></div>
            )}
          </div>
        }
        overviewContent={
          <OverviewContent 
            title="Natural Gas Overview" 
            description="Residential and commercial natural gas consumption over time."
          >
            <p>
              {gasStacked && gasRes && gasRes.chartData && gasCom && gasCom.chartData ? (() => {
                const metrics = calculateNarrativeMetrics(gasStacked);
                if (!metrics) return `Loading natural gas data for ${currentLocation}...`;
                
                return (
                  <>
                    Total natural gas consumption in {currentLocation} reached {metrics.latestValue.toLocaleString()} therms in {metrics.latestYear}, 
                    representing a {metrics.changePercent > 0 ? 'increase' : 'decrease'} of {Math.abs(metrics.changePercent)}% since {metrics.earliestYear} 
                    ({metrics.earliestValue.toLocaleString()} therms). 
                    {metrics.changePercent < 0 ? 'This reduction reflects successful energy efficiency measures and building electrification efforts. ' : 
                     metrics.changePercent > 10 ? 'This significant increase indicates growing natural gas demand that may require additional efficiency measures. ' : 
                     'This moderate change suggests relatively stable natural gas consumption patterns. '}
                    The data includes {metrics.yearsObserved} years of observed measurements out of {metrics.totalYears} total years of data, 
                    {metrics.yearsObserved > metrics.totalYears * 0.7 ? 'providing a strong foundation for analysis.' : 'with some years relying on estimated values.'}
                  </>
                );
              })() : `Loading natural gas data for ${currentLocation}...`}
            </p>
          </OverviewContent>
        }
        relevanceContent={<RelevanceContent title="Relevance" description="Natural gas use is a major source of building emissions; tracking helps target electrification and efficiency." />}
        equityContent={<EquityContent title="Equity" description="Programs should mitigate energy burden and prioritize upgrades in underserved communities." />}
        methodologyContent={
          <MethodologyContent 
            title="Methodology" 
            description="Natural gas consumption data for residential and commercial sectors over time." 
            dataSources={
              gasMethodology?.dataSources && gasMethodology.dataSources.length > 0
                ? gasMethodology.dataSources
                : ["PG&E (Pacific Gas & Electric)", "SoCalGas (Southern California Gas Company)", "SCE (Southern California Edison)", "SDGE (San Diego Gas & Electric)"]
            } 
            methodology={
              gasMethodology?.methods && gasMethodology.methods.length > 0
                ? gasMethodology.methods
                : [
                    "Data provenance indicated by measurement column (observed, interpolated, extrapolated)",
                    "Stacked bars sum residential and commercial by year",
                    "Linear interpolation used for missing values between observed data points",
                    "Linear extrapolation used for forecasting future years and backcasting historical years when data not available",
                    "Colors indicate data source: observed (dark), interpolated (medium), extrapolated (light)"
                  ]
            } 
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        className="alternating-bg"
        dataTableContent={gasShowDataTable ? (
          <div>
            {gasModifiedYears.size > 0 && (
              <div className="modified-years-info" style={{ marginBottom: '16px' }}>
                <small>
                  <strong>Modified years:</strong> {Array.from(gasModifiedYears).sort().join(', ')} 
                  (other years scaled proportionally)
                </small>
              </div>
            )}
            <DataTable
              data={gasTableData}
              highlightedRows={gasModifiedYears}
              highlightKey="year"
              columns={[
                { key: 'year', label: 'Year', type: 'year', editable: true, 
                  validate: (value) => {
                    const year = parseInt(value);
                    return (year >= 2005 && year <= 2023) ? null : 'Year must be between 2005-2023';
                  }
                },
                { key: 'residential', label: 'Residential (therms)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'commercial', label: 'Commercial (therms)', type: 'number', editable: true,
                  validate: (value) => {
                    const num = parseFloat(value);
                    return (!isNaN(num) && num >= 0) ? null : 'Value must be a positive number';
                  }
                },
                { key: 'total', label: 'Total (therms)', type: 'number', editable: false },
                { key: 'location', label: 'Location', type: 'text', editable: false }
              ]}
              onDataChange={handleGasDataTableChange}
              onRestoreDefaults={handleGasRestoreDefaults}
              title="Natural Gas Data"
              maxHeight="500px"
              pageSize={15}
            />
          </div>
        ) : null}
        showDataTable={gasShowDataTable}
        onToggleDataTable={toggleGasDataTable}
        onDownloadPNG={handleGasDownloadPNG}
        onDownloadCSV={handleGasDownloadCSV}
        pngFilename={`Natural_Gas_${gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita'}_Chart`}
        csvFilename={`Natural_Gas_${gasView === 'res' ? 'Residential' : gasView === 'com' ? 'Commercial' : gasView === 'total' ? 'Total' : 'Per_Capita'}_Data`}
      />
      
      {/* Solar PV Capacity & Projects (combined) */}
      <ChartLayout
        title="Solar PV Installations"
        description="Toggle between capacity, cost, cumulative capacity, and per capita comparison views. Charts show solar PV adoption trends and grid decarbonization progress."
        chartType="bar"
        chartSelector={{
          grouped: true,
          groups: [
            {
              category: 'capacity',
              label: 'Capacity',
              options: [
                { value: 'capacity', label: 'By Year' },
                { value: 'cumulative', label: 'Cummulative' },
                { value: 'perCapita', label: 'Per Capita' }
              ]
            },
            {
              category: 'cost',
              label: 'Cost',
              options: [
                { value: 'batteryCost', label: 'Residential' },
                { value: 'cost', label: 'Sectors' }
              ]
            },
            {
              category: 'installers',
              label: 'Installers',
              options: [
                { value: 'installers', label: 'Capacity' },
                { value: 'installersCost', label: 'Cost' }
              ]
            }
          ],
          value: pvView,
          onChange: (value) => setPvView(value as 'capacity' | 'cost' | 'cumulative' | 'perCapita' | 'batteryCost' | 'installers' | 'installersCost')
        }}
        keyMetrics={
          pvView === 'cost' ? (
            <KeyMetrics>
              <KeyMetric 
                value={costData?.avgCost ? `$${costData.avgCost.toFixed(2)}` : '$0.00'} 
                label="Average cost per watt"
                color="#1e3a8a"
              />
              <KeyMetric 
                value={costData?.latestYearCost ? `$${costData.latestYearCost.toFixed(2)}` : '$0.00'} 
                label="Cost in most recent year"
                color="#10b981"
              />
              <KeyMetric 
                value={costData?.costReduction ? `${costData.costReduction.toFixed(1)}%` : '0%'} 
                label="Cost reduction since 2010"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'batteryCost' ? (
            <KeyMetrics>
              <KeyMetric 
                value={batteryCostData?.avgPVCost ? `$${Math.round(batteryCostData.avgPVCost)}/kW` : '$0/kW'} 
                label="Average PV cost"
                color="#3b82f6"
              />
              <KeyMetric 
                value={batteryCostData?.avgBatteryCost ? `$${Math.round(batteryCostData.avgBatteryCost)}/kW` : '$0/kW'} 
                label="Average PV+Battery cost"
                color="#10b981"
              />
              <KeyMetric 
                value={batteryCostData?.avgBatteryCost && batteryCostData?.avgPVCost ? 
                  `$${Math.round(batteryCostData.avgBatteryCost - batteryCostData.avgPVCost)}/kW` : '$0/kW'} 
                label="Battery premium"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'installers' ? (
            <KeyMetrics>
              <KeyMetric 
                value={installersData?.totalInstallers ? `${installersData.totalInstallers}` : '0'} 
                label="Top pairs"
                color="#3b82f6"
              />
              <KeyMetric 
                value={installersData?.topInstaller ? installersData.topInstaller : 'N/A'} 
                label="Leading pair"
                color="#10b981"
              />
              <KeyMetric 
                value={installersData?.topInstallerKw ? `${installersData.topInstallerKw.toFixed(1)} kW` : '0 kW'} 
                label="Top pair capacity"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : pvView === 'installersCost' ? (
            <KeyMetrics>
              <KeyMetric 
                value={installersCostData?.totalPairs ? `${installersCostData.totalPairs}` : '0'} 
                label="Top pairs"
                color="#3b82f6"
              />
              <KeyMetric 
                value={installersCostData?.topPair ? installersCostData.topPair : 'N/A'} 
                label="Leading pair"
                color="#10b981"
              />
              <KeyMetric 
                value={installersCostData?.avgCost ? `$${installersCostData.avgCost.toFixed(2)}/W` : '$0.00/W'} 
                label="Average cost per watt"
                color="#f59e0b"
              />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric value={capacityData?.totalCapacity ? `${capacityData.totalCapacity.toFixed(1)} MW` : '—'} label="Total capacity" />
              <KeyMetric value={projectCountData?.totalProjects ? `${projectCountData.totalProjects.toLocaleString()}` : '—'} label="Total projects" />
              <KeyMetric value={capacityData?.latestYear || projectCountData?.latestYear || '—'} label="Latest year" />
            </KeyMetrics>
          )
        }
        chartContent={
          pvView === 'cost' ? (
            costData ? (
              <ChartRendererSupabase
                chartData={costData.chartData}
                chartOptions={costData.chartOptions}
                height="380px"
                title={`Solar PV Cost per Watt - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Cost data...</p>
              </div>
            )
          ) : pvView === 'cumulative' ? (
            selectedGeofips ? (
              <CumulativeSolarCapacityChart location={currentLocation} geofips={selectedGeofips} />
            ) : (
              <div className="placeholder-chart">
                <p>Loading location data...</p>
              </div>
            )
          ) : pvView === 'perCapita' ? (
            selectedGeofips ? (
              <SolarPerCapitaComparisonChart location={currentLocation} geofips={selectedGeofips} />
            ) : (
              <div className="placeholder-chart">
                <p>Loading location data...</p>
              </div>
            )
          ) : pvView === 'batteryCost' ? (
            batteryCostData ? (
              <ChartRendererSupabase
                chartData={batteryCostData.chartData}
                chartOptions={batteryCostData.chartOptions}
                height="380px"
                title={`Solar PV Cost (Residential) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Battery Cost data...</p>
              </div>
            )
          ) : pvView === 'installers' ? (
            installersData ? (
              <ChartRendererSupabase
                chartData={installersData.chartData}
                chartOptions={{
                  ...installersData.chartOptions,
                  type: 'bar'
                }}
                height="550px"
                title={`Top Solar PV Installer-Manufacturer Pairs (Capacity) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Installers data...</p>
              </div>
            )
          ) : pvView === 'installersCost' ? (
            installersCostData ? (
              <ChartRendererSupabase
                chartData={installersCostData.chartData}
                chartOptions={{
                  ...installersCostData.chartOptions,
                  type: 'bar'
                }}
                height="550px"
                title={`Top Solar PV Installer-Manufacturer Pairs (Cost) - ${currentLocation}`}
                location={currentLocation}
              />
            ) : (
              <div className="placeholder-chart">
                <p>Loading Solar PV Installers Cost data...</p>
              </div>
            )
          ) : capacityData ? (
            <div>
              {pvView === 'capacity' && (
            <ChartRendererSupabase
              chartData={capacityData.chartData}
                  chartOptions={{
                    ...capacityData.chartOptions,
                    type: 'bar',
                    scales: {
                      ...(capacityData.chartOptions?.scales || {}),
                      x: { stacked: true },
                      y: { stacked: true, title: { display: true, text: 'Capacity (MW)' } }
                    }
                  }}
                  height="380px"
                  title={`Solar PV Capacity (Stacked) - ${currentLocation}`}
                  location={currentLocation}
                />
              )}
            </div>
          ) : (
            <div className="placeholder-chart"><p>Loading Solar PV data…</p></div>
          )
        }
        overviewContent={
          pvView === 'cost' ? (
            <OverviewContent
              title="Solar PV Cost Overview"
              description="This chart displays the average cost per watt for solar PV installations by sector over time."
            >
              <p>{costData?.narrative || 'Select a location to view Solar PV Cost insights and trends.'}</p>
            </OverviewContent>
          ) : pvView === 'cumulative' ? (
            <OverviewContent
              title="Cumulative Solar Capacity Overview"
              description="This chart shows cumulative solar capacity by sector over time, demonstrating grid decarbonization progress."
            >
              <p>Track the total installed solar capacity accumulated over time, broken down by sector (residential, commercial, utility). This cumulative view shows how solar adoption is building toward grid decarbonization goals.</p>
            </OverviewContent>
          ) : pvView === 'perCapita' ? (
            <OverviewContent
              title="Solar Per Capita Comparison Overview"
              description="Compare solar capacity per capita between the selected location and California average."
            >
              <p>See how solar adoption per person compares between {currentLocation} and California's average. This metric helps understand relative solar penetration and adoption rates across different communities.</p>
            </OverviewContent>
          ) : pvView === 'batteryCost' ? (
            <OverviewContent
              title="Solar PV Battery Cost Overview"
              description="Compare costs between standalone photovoltaic systems and photovoltaic systems with battery energy storage."
            >
              <p>{batteryCostData?.narrative || 'Select a location to view Solar PV Battery Cost insights and trends.'}</p>
            </OverviewContent>
          ) : pvView === 'installers' ? (
            <OverviewContent
              title="Top Solar PV Installer-Manufacturer Pairs (Capacity) Overview"
              description="View the top 10 solar PV installer-manufacturer pairs by total installed capacity (kW) for this location."
            >
              <p>{installersData?.narrative || 'Select a location to view top Solar PV Installer-Manufacturer pairs and their installed capacity.'}</p>
            </OverviewContent>
          ) : pvView === 'installersCost' ? (
            <OverviewContent
              title="Top Solar PV Installer-Manufacturer Pairs (Cost) Overview"
              description="View the top 10 solar PV installer-manufacturer pairs by cost per watt for this location."
            >
              <p>{installersCostData?.narrative || 'Select a location to view top Solar PV Installer-Manufacturer pairs and their cost per watt.'}</p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Solar PV Overview"
              description={pvView === 'capacity' ? 'Installed capacity (MW) by sector over time.' : 'Number of solar PV projects by sector over time.'}
            >
              <p>{pvView === 'capacity' ? (capacityData?.narrative || 'Select a location to view Solar PV capacity insights and trends.') : (projectCountData?.narrative || 'Select a location to view Solar PV project insights and trends.')}</p>
            </OverviewContent>
          )
        }
        relevanceContent={
          pvView === 'cost' ? (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Understanding solar PV cost trends is crucial for planning cost-effective renewable energy deployment."
            >
              <div className="methodology-details-simple">
                <h5>Cost-Effectiveness</h5>
                 <p>Lower costs make solar PV more accessible and accelerate renewable energy adoption.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Policy Planning</h5>
                <p>Cost trends inform incentive programs and financing options for solar PV installations.</p>
              </div>
            </RelevanceContent>
          ) : pvView === 'cumulative' ? (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Cumulative solar capacity tracks progress toward grid decarbonization and renewable energy goals."
            >
              <div className="methodology-details-simple">
                <h5>Grid Decarbonization</h5>
                <p>Cumulative capacity shows how solar adoption is reducing reliance on fossil fuel-based electricity generation.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Progress Tracking</h5>
                <p>This metric helps communities monitor progress toward renewable energy targets and climate action goals.</p>
              </div>
            </RelevanceContent>
          ) : pvView === 'perCapita' ? (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Per capita solar capacity helps compare adoption rates and identify opportunities for equitable access."
            >
              <div className="methodology-details-simple">
                <h5>Adoption Comparison</h5>
                <p>Compare solar penetration rates between communities to identify leaders and areas needing support.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Equity Analysis</h5>
                <p>Per capita metrics help ensure solar benefits reach all communities, especially underserved areas.</p>
              </div>
            </RelevanceContent>
          ) : (
            <RelevanceContent title="Relevance to Climate Action" description="Capacity reflects clean energy output potential; project counts reflect market adoption." />
          )
        }
        equityContent={
          pvView === 'cost' ? (
            <EquityContent
              title="Equity Considerations"
              description="Solar PV costs can create barriers to access, requiring targeted solutions for underserved communities."
            >
              <div className="methodology-details-simple">
                <h5>Affordability Barriers</h5>
                <p>High upfront costs can prevent low-income households from accessing solar PV benefits.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Financing Solutions</h5>
                <p>Innovative financing models are needed to ensure solar PV access across all income levels.</p>
              </div>
            </EquityContent>
          ) : pvView === 'cumulative' ? (
            <EquityContent
              title="Equity Considerations"
              description="Ensure cumulative solar benefits reach all communities, especially those most affected by energy costs."
            >
              <div className="methodology-details-simple">
                <h5>Community Access</h5>
                <p>Cumulative capacity growth should benefit all neighborhoods, not just affluent areas with higher solar adoption.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Energy Justice</h5>
                <p>Grid decarbonization through solar should reduce energy burdens for low-income communities.</p>
              </div>
            </EquityContent>
          ) : pvView === 'perCapita' ? (
            <EquityContent
              title="Equity Considerations"
              description="Per capita metrics help identify disparities in solar access and adoption across communities."
            >
              <div className="methodology-details-simple">
                <h5>Access Disparities</h5>
                <p>Lower per capita rates may indicate barriers to solar access that require targeted programs.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Community Solar</h5>
                <p>Community solar programs can help increase per capita adoption in areas with limited rooftop access.</p>
              </div>
            </EquityContent>
          ) : (
            <EquityContent title="Equity Considerations" description="Ensure access to rooftop solar benefits across income levels and housing types." />
          )
        }
        methodologyContent={
          pvView === 'cost' ? (
            <MethodologyContent
              title="Methodology"
              description="Solar PV cost data comes from California Distributed Generation Statistics, providing insights into cost trends and sector differences."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Applications Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Includes all NEM systems regardless of technology type or program',
                'California Solar Initiative incentive program data and other publicly available incentive program data sets'
              ]}
              methodology={[
                'Cost per watt calculated as total cost divided by total capacity',
                'Averages computed by sector and year',
                'Data aggregated from individual installation records'
              ]}
            />
          ) : pvView === 'cumulative' ? (
            <MethodologyContent
              title="Methodology"
              description="Cumulative solar capacity calculated by summing annual installations over time, broken down by sector."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Project Sites Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Data covers 1982 to present'
              ]}
              methodology={[
                'Cumulative capacity calculated by summing annual installations over time',
                'Data grouped by sector and year',
                'Capacity values accumulated from earliest year to present'
              ]}
            />
          ) : pvView === 'perCapita' ? (
            <MethodologyContent
              title="Methodology"
              description="Per capita solar capacity calculated using total capacity divided by population, comparing location to California average."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Project Sites Data Set from PG&E, SCE, and SDG&E',
                'Population data from population table (U.S. Census Bureau)',
                'Location data matched by geo_fips codes'
              ]}
              methodology={[
                'Per capita metrics calculated using total capacity divided by population',
                'Cumulative capacity calculated by summing annual installations over time',
                'California comparison uses state-level aggregated data'
              ]}
            />
          ) : pvView === 'batteryCost' ? (
            <MethodologyContent
              title="Methodology"
              description="Residential solar PV cost comparison between standalone photovoltaic systems and photovoltaic systems with battery energy storage."
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Applications Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Technology type classifications: "Photovoltaic" and "Photovoltaic, Battery Energy Storage"',
                'Residential sector data only'
              ]}
              methodology={[
                'Cost per kW calculated as total cost divided by system size (kW)',
                'Data filtered for Residential sector and system size > 0',
                'Average cost per kW calculated by geoid, location_name, county, year, and technology type',
                'Battery premium calculated as difference between PV+Battery cost and standalone PV cost'
              ]}
            />
          ) : pvView === 'installers' || pvView === 'installersCost' ? (
            <MethodologyContent
              title="Methodology"
              description={pvView === 'installers' 
                ? "Top 10 installer-manufacturer pairs by total installed capacity (kW) for this location."
                : "Top 10 installer-manufacturer pairs by cost per watt for this location."}
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Applications Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Installer and manufacturer information from project records'
              ]}
              methodology={[
                pvView === 'installers' 
                  ? 'Top 10 pairs selected by total kW installed (system size)'
                  : 'Top 10 pairs selected by total kW installed, then cost per watt calculated as total cost divided by total kW',
                'Installer and manufacturer names standardized using fuzzy matching',
                'Data grouped by geoid, location_name, county, installer, and manufacturer',
                'For each geographic level, top 10 entries selected based on total_kw'
              ]}
            />
          ) : (
            <MethodologyContent 
              title="Methodology" 
              description="Solar installation records by sector; stacked columns sum sectors per year." 
              dataSources={[
                'California Distributed Generation Statistics (https://www.californiadgstats.ca.gov/downloads/)',
                'Interconnected Project Sites Data Set from PG&E, SCE, and SDG&E under Rule 21 tariffs (per CPUC Decision D.14-11-001)',
                'Includes all NEM systems regardless of technology type or program',
                'Data covers 1982 to present'
              ]} 
              methodology={[
                'Capacity from project records in the Interconnected Project Sites Data Set',
                'Each row represents one interconnection address/project site',
                'Data grouped by sector and year'
              ]} 
            />
          )
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          const viewLabel = pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : 'Per_Capita';
          const canvas = document.querySelector(`#solar-pv-${pvView} canvas`) as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Solar_PV_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          const viewLabel = pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : 'Per_Capita';
          console.log(`CSV download for Solar PV ${viewLabel} not yet implemented`);
        }}
        pngFilename={`Solar_PV_${pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : 'Per_Capita'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Solar_PV_${pvView === 'capacity' ? 'Capacity' : pvView === 'cost' ? 'Cost' : pvView === 'cumulative' ? 'Cumulative' : 'Per_Capita'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      {/* Heat Pump Adoption Section */}
      <ChartLayout
        title="Heat Pump Adoption"
        chartType="line"
        chartSelector={{
          options: [
            { value: 'units', label: 'Units Installed' },
            { value: 'perCapita', label: 'Per Capita' },
            { value: 'cost', label: 'Installation Costs' },
            { value: 'technology', label: 'Cost by Technology' }
          ],
          value: heatPumpView,
          onChange: (value) => {
            setHeatPumpView(value as 'cost' | 'technology' | 'units' | 'perCapita');
          }
        }}
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={heatPumpMetrics ? heatPumpMetrics.totalUnits.toLocaleString() : "Loading..."} 
              label="Total Heat Pump Units"
              color="#3b82f6"
            />
            <KeyMetric 
              value={heatPumpMetrics ? `${heatPumpMetrics.totalGHGSavings.toFixed(1)} MT CO₂e` : "Loading..."} 
              label="Annual GHG Savings"
              color="#10b981"
            />
            <KeyMetric 
              value={heatPumpMetrics ? `$${heatPumpMetrics.averageCostPerUnit.toLocaleString()}` : "Loading..."} 
              label="Average Cost per Unit"
              color="#f59e0b"
            />
            <KeyMetric 
              value={heatPumpMetrics ? `${heatPumpMetrics.growthRate > 0 ? '+' : ''}${heatPumpMetrics.growthRate.toFixed(1)}%` : "Loading..."} 
              label="Growth Rate"
              color="#8b5cf6"
            />
          </KeyMetrics>
        }
        chartContent={
          <div>
            {selectedGeofips ? (
              heatPumpView === 'units' ? (
                <HeatPumpUnitsChart location={currentLocation} geofips={selectedGeofips} />
              ) : heatPumpView === 'perCapita' ? (
                <HeatPumpPerCapitaChart location={currentLocation} geofips={selectedGeofips} />
              ) : heatPumpView === 'cost' ? (
                <HeatPumpCostChart location={currentLocation} geofips={selectedGeofips} />
              ) : (
                <HeatPumpTechnologyChart location={currentLocation} geofips={selectedGeofips} />
              )
            ) : (
              <div>Loading location data...</div>
            )}
          </div>
        }
        overviewContent={
          <OverviewContent
            title="Heat Pump Adoption Overview"
            description="Heat pump installation trends and costs for HVAC and water heating systems."
          >
            <p>
              {heatPumpMetrics ? (() => {
                const { totalUnits, totalGHGSavings, averageCostPerUnit, growthRate, hvacUnits, hpwhUnits, firstYear, lastYear, yearsObserved } = heatPumpMetrics;
                
                return (
                  <>
                    Heat pump adoption in {currentLocation} has shown significant progress, with <strong>{totalUnits.toLocaleString()} total units</strong> installed 
                    from {firstYear} to {lastYear} according to the TECH Clean California database. This represents a <strong>{growthRate > 0 ? 'growth' : 'change'} of {Math.abs(growthRate).toFixed(1)}%</strong> over {yearsObserved} years of data.
                    <br /><br />
                    <strong>Important Note:</strong> These totals reflect installations in the TECH Clean California database only, which tracks installations that received incentives through the program. 
                    This does not represent all heat pump installations in {currentLocation}, as many installations may occur without program incentives.
                    <br /><br />
                    The installations include <strong>{hvacUnits.toLocaleString()} HVAC systems</strong> and <strong>{hpwhUnits.toLocaleString()} heat pump water heaters</strong>, 
                    with an average cost of <strong>${averageCostPerUnit.toLocaleString()}</strong> per unit. These installations are projected to save 
                    <strong> {totalGHGSavings.toFixed(1)} metric tons of CO₂e annually</strong>, contributing significantly to {currentLocation}'s climate goals.
                    <br /><br />
                    Heat pumps are highly efficient electric systems that can provide both heating and cooling, making them a key technology for building electrification. 
                    The TECH Clean California program gathers and publishes data on heat pump water heating (HPWH) and heat pump heating, ventilation, and air-conditioning (HP HVAC) markets in California, 
                    as well as the experience of people who install heat pumps in their homes.
                  </>
                );
              })() : `Loading heat pump data for ${currentLocation}...`}
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Heat pumps are essential for building electrification and reducing greenhouse gas emissions."
          >
            <div className="methodology-details-simple">
              <h5>Emission Reductions</h5>
              <p>Heat pumps can reduce building emissions by 50-70% compared to gas heating systems.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Energy Efficiency</h5>
              <p>Heat pumps are 3-4 times more efficient than traditional heating systems.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Ensure heat pump benefits reach all communities, especially those most affected by energy costs."
          >
            <div className="methodology-details-simple">
              <h5>Cost Barriers</h5>
              <p>Upfront costs can be a barrier; financing programs and incentives are crucial for equitable access.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Energy Burden</h5>
              <p>Heat pumps can reduce energy costs for low-income households over time.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Heat pump installation data from the TECH Clean California database, which tracks installations receiving program incentives."
            dataSources={[
              '<strong>TECH Clean California Database</strong> - <a href="https://techcleanca.com/heat-pump-data/" target="_blank" rel="noopener noreferrer">https://techcleanca.com/heat-pump-data/</a>',
              'The TECH Clean California program gathers and publishes data on heat pump water heating (HPWH) and heat pump heating, ventilation, and air-conditioning (HP HVAC) markets in California',
              'All data is anonymized and aggregated per California Public Utility Commission (CPUC) guidelines in Decision 11-07-056, Decision 14-05-016, Decision 20-03-027, and Decision 21-11-002',
              'Heat pump installation records by location and year from participating contractors',
              'Cost data by system type (HVAC, HPWH)',
              'Building type and geography classifications'
            ]}
            methodology={[
              'Data aggregated from installations that received incentives through the TECH Clean California program',
              'Only installations participating in the incentive program are included in this database',
              'This represents a subset of all heat pump installations, as many installations occur without program incentives',
              'Costs calculated per unit for each system type',
              'Location data matched by geo_fips codes',
              'Annual trends calculated from installation records in the TECH database'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          const viewLabel = heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed';
          const canvas = document.querySelector(`#heat-pump-${heatPumpView} canvas`) as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Heat_Pump_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          const viewLabel = heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed';
          console.log(`CSV download for Heat Pump ${viewLabel} not yet implemented`);
        }}
        pngFilename={`Heat_Pump_${heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Heat_Pump_${heatPumpView === 'cost' ? 'Installation_Costs' : heatPumpView === 'technology' ? 'Technology_Costs' : heatPumpView === 'perCapita' ? 'Per_Capita' : 'Units_Installed'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      {/* Housing to Employees Ratio */}
      <HousingToEmployees
        location={currentLocation}
        geofips={selectedGeofips}
        preferredState="California"
      />

      {/* Single Family Detached Housing Chart */}
      <SingleFamilyDetached 
        location={currentLocation}
        geofips={selectedGeofips}
      />

      {/* Owner-Occupied Housing Map */}
      <ChartLayout
        title="Owner-Occupied Housing Map"
        chartType="map"
        keyMetrics={
          ownerOccupiedHousingMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${ownerOccupiedHousingMetrics.currentValue.toFixed(1)}%`}
                label="Owner-Occupied Housing"
              />
              <KeyMetric
                value={`${ownerOccupiedHousingMetrics.californiaAverage.toFixed(1)}%`}
                label="California Average"
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <GenericMapTiered 
            location={currentLocation}
            lockedVariable="owner_occupied_housing"
            hideVariableSelector={true}
            onDataLoaded={(metrics) => {
              setOwnerOccupiedHousingMetrics(metrics);
            }}
          />
        }
        overviewContent={
          ownerOccupiedHousingMetrics ? (
            <OverviewContent
              title="Owner-Occupied Housing Overview"
              description="Geographic visualization of owner-occupied housing percentage by census tract. This analysis examines housing tenure patterns and their relationship to building energy use, electrification potential, and climate action strategies."
            >
              {[
                `${currentLocation} has ${ownerOccupiedHousingMetrics.currentValue.toFixed(1)}% owner-occupied housing, which is ${ownerOccupiedHousingMetrics.currentValue > ownerOccupiedHousingMetrics.californiaAverage ? 'higher' : 'lower'} than the California average of ${ownerOccupiedHousingMetrics.californiaAverage.toFixed(1)}%.`,
                `Owner-occupied housing represents households where residents own their homes, which can influence the ability to make energy efficiency improvements, install solar PV, and electrify building systems. Higher owner-occupancy rates may indicate greater potential for building decarbonization investments.`,
                `The data is from ${ownerOccupiedHousingMetrics.year} American Community Survey 5-Year Estimates, providing reliable demographic and housing tenure information.`
              ]}
            </OverviewContent>
          ) : undefined
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action Policy"
            description="How owner-occupied housing data informs climate action planning and building decarbonization strategies."
          >
            {[
              'Owner-occupied housing data helps identify areas where residents have direct control over building improvements, guiding targeted efficiency and electrification programs.',
              'Understanding housing tenure patterns supports program design that addresses both owner-occupied and rental housing needs for comprehensive building decarbonization.',
              'Owner-occupancy rates inform solar PV and heat pump incentive programs, as homeowners may have more flexibility to make building improvements than renters.',
              'Housing tenure data helps prioritize programs that address split incentives between landlords and tenants, ensuring both owner-occupied and rental housing benefit from climate investments.'
            ]}
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Equity implications and considerations for owner-occupied housing data in climate planning."
          >
            {[
              'Owner-occupied housing patterns reflect historical policies and economic conditions that have shaped housing access and wealth accumulation, requiring equitable approaches to building decarbonization.',
              'Rental housing often faces barriers to efficiency improvements due to split incentives, necessitating programs that address both owner and renter needs.',
              'Low-income homeowners may lack resources for building improvements despite owning their homes, requiring targeted financial assistance and incentive programs.',
              'Equitable climate action requires ensuring that both owner-occupied and rental housing benefit from building efficiency and electrification programs, addressing the needs of all residents regardless of tenure status.'
            ]}
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Owner-Occupied Housing analysis based on American Community Survey data with geographic visualization."
            dataSources={[
              'American Community Survey 5-Year Estimates (ACS5)',
              'U.S. Census Bureau demographic and housing data',
              'California census tract boundaries',
              'Housing tenure data (owner-occupied vs. renter-occupied)'
            ]}
            methodology={[
              'Owner-occupied housing percentage calculated as owner-occupied units divided by total occupied housing units',
              'Data represents housing tenure patterns by census tract from American Community Survey',
              'Geographic visualization shows owner-occupancy rates across census tracts',
              'Comparison calculated relative to California state average',
              '5-year estimates provide more reliable data for smaller geographic areas'
            ]}
          />
        }
        activeView={activeOwnerOccupiedView}
        onViewChange={setActiveOwnerOccupiedView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          console.log('Map PNG download - to be implemented with html2canvas');
        }}
        onDownloadCSV={() => {
          console.log('Map CSV download - to be implemented in GenericMapTiered');
        }}
        pngFilename={`Owner_Occupied_Housing_Map_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Owner_Occupied_Housing_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      {/* Market Rent Chart */}
      <ChartLayout
        title="Housing Units by Rent Category"
        chartType={marketRentChartView === 'stacked' ? 'line' : 'line'}
        chartContent={
          <MarketRentChart key={`${currentLocation}-${marketRentChartView}`} location={currentLocation} chartType={marketRentChartView} />
        }
        chartSelector={{
          options: [
            { value: 'stacked', label: 'Stacked' },
            { value: 'line', label: 'Line Chart' }
          ],
          value: marketRentChartView,
          onChange: (value) => setMarketRentChartView(value as 'line' | 'stacked')
        }}
        overviewContent={
          <OverviewContent
            title="Market Rent Distribution Overview"
            description="Line or stacked area chart showing the number of housing units in each rent category over time. This data helps understand housing affordability distribution and its relationship to building energy use and climate action planning."
          >
            {[
              `This chart displays the number of housing units in each rent category (e.g., under $500, $500-$999, $1,000-$1,499, etc.) for ${currentLocation} over time.`,
              `The line chart view shows individual trends for each rent category, while the stacked view shows the total housing units and the proportion of each category.`,
              `Understanding the distribution of housing units across rent categories helps identify areas where housing costs may impact residents' ability to invest in energy efficiency improvements or electrification.`,
              `The data shows how many housing units fall into each rent range, providing insight into the housing affordability landscape and its evolution over time.`,
              `Note: Categories are standardized for consistency. For 2010-2014, "$1,500 or more" is shown as "$1,500 and Over" (more accurate to source data). For 2015+, the standard "$1,500-$1,999" category is used. The "$500-$999" category is combined for all years.`
            ]}
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action Policy"
            description="How housing unit distribution by rent category informs climate action planning and building decarbonization strategies."
          >
            {[
              'The distribution of housing units across rent categories helps identify communities where housing costs may limit residents\' ability to invest in energy efficiency and electrification, guiding targeted assistance programs.',
              'Understanding how many units fall into different rent ranges supports program design that addresses both building improvements and resident financial capacity.',
              'Housing unit distribution data helps prioritize climate investments in communities where residents may face barriers to making building improvements due to housing costs.',
              'Housing affordability distribution informs equitable climate action strategies that ensure all residents can benefit from building decarbonization programs.'
            ]}
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Equity implications and considerations for housing unit distribution by rent category in climate planning."
          >
            {[
              'The distribution of housing units across rent categories reflects historical policies and economic conditions that have shaped housing access and affordability, requiring equitable approaches to building decarbonization.',
              'Communities with many units in lower rent categories may face greater barriers to investing in energy efficiency improvements, necessitating targeted financial assistance programs.',
              'Equitable climate action requires ensuring that all residents, regardless of their rent category, can benefit from building efficiency and electrification programs.',
              'Housing unit distribution data helps identify communities where climate investments must address both building improvements and housing affordability to ensure equitable outcomes.'
            ]}
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Housing unit distribution analysis based on market rent data with line or stacked area chart visualization."
            dataSources={[
              'Supabase `market_rent_percent` table',
              'Data aggregated by year and standardized rent category',
              'Geographic filtering by `geofips`',
              'Source: American Community Survey (ACS) DP04 table'
            ]}
            methodology={[
              'Data is fetched from the `market_rent_percent` table for the selected location\'s `geofips`.',
              'Raw ACS data categories changed in 2015. To ensure consistency across all years (2010-2022), categories are standardized to the 2015+ structure.',
              'Specifically, for years prior to 2015, the original "$500 to $749" and "$750 to $999" categories are combined into a single "$500 to $999" category to match the 2015+ data structure.',
              'The line chart displays the number of housing units over time for each standardized category as separate lines.',
              'The stacked area chart displays the same data with categories stacked to show total housing units and the proportion of each category.',
              'Missing data points are treated as zero for visualization purposes, but actual nulls are handled during data processing.'
            ]}
          />
        }
        activeView="overview"
        onViewChange={() => {}}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          console.log('Market Rent PNG download - to be implemented');
        }}
        onDownloadCSV={() => {
          console.log('Market Rent CSV download - to be implemented');
        }}
        pngFilename={`Housing_Units_by_Rent_Category_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Housing_Units_by_Rent_Category_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      {/* Multifamily Energy Efficiency Targeting Tool */}
      <BayRENMultifamilyTargetingTool />

      {/* Multifamily Energy Efficiency Targeting Tool - California Data */}
      <CaliforniaBayRENTool />
    </div>
  );
};


